// ============================================================================
// APPOINTMENT MODAL
// Appointment scheduling modal - each appointment is a unique booking that can
// be shared across multiple assigned team members at the same time slot.
// Includes: 3-way Contact/Location/Person search, Job search, Appointment scheduling
// Used by Quick Actions in Total Operations
// Date: December 2025 | Updated: February 2026
// ============================================================================

'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-operations/workflows/service-operations/appointments.mdx
 * (published at user-docs.totalaccess.co.za/total-operations/workflows/service-operations/appointments#book-an-appointment)
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  X, Calendar, Clock, User, MapPin, Briefcase, Target, Search,
  Building2, ChevronDown, Check, Loader2, Mail, Phone, Users,
  AlertTriangle, Info, Plus, Lock, Package,
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { authService } from '@/lib/authService';
import { isCapacitor } from '@/lib/platformUtils';
import { emit } from '@/lib/typedEventBus';
import { fetchContactPeople as fetchGuardedContactPeople } from '@/lib/contacts/fetchContactPeople';
import { notify, confirm } from '@/lib/notifications';
import { emitOperationsGuidedTourTrainingEvent } from '@/lib/operations/guidedTourEvents';
import QuickLocationForm from '@/components/work-items/QuickLocationForm';
import type { CreatedLocation } from '@/components/work-items/QuickLocationForm';
import AppointmentScheduler, { getDefaultAppointmentSchedule } from '@/components/work-items/AppointmentScheduler';
import PurchaseItemList, { type PurchaseItemListHandle } from '@/components/work-items/PurchaseItemList';
import { normalizePurchaseListItems, type PurchaseListItem } from '@/components/work-items/purchaseListUtils';
import LocationAutocomplete, { type CustomLocationData, CustomLocationResult } from '@/components/maps/LocationAutocomplete';
import UnifiedContactLookup, {
  type UnifiedLookupContactPerson,
  type UnifiedLookupLocation,
} from '@/components/shared/UnifiedContactLookup';
import { DEFAULT_APPOINTMENT_TYPES, useJobConfig } from '@/hooks/useJobConfig';
import type { JobType as ConfigurableTypeOption } from '@/hooks/useJobConfig';
import { fetchWorkflowTemplate } from '@/app/client-zone/modules/total-operations/components/smart-task/utils/fetchWorkflowTemplate';
import { useOperationsAssignmentOptions } from '@/hooks/useOperationsAssignmentOptions';
import { buildRecurringAppointmentOccurrences, type AppointmentRecurrenceRule } from '@/lib/work-items/appointmentRecurrence';
import { findAppointmentClashes, isScheduleHiddenAppointmentStatus } from '@/lib/work-items/appointmentClashDetection';
import { getAppointmentScheduleErrorMessage, getAppointmentScheduleValidation } from '@/lib/work-items/appointmentSchedule';
import { normalizeAppointmentTypeKey, PURCHASE_APPOINTMENT_TYPE_KEY } from '@/lib/work-items/appointmentTypeConfig';
import {
  buildMissingCoordinatesMessage,
  extractCoordinates,
  getContactLocationCoordinates,
  hasContactLocationCoordinates,
} from '@/lib/work-items/appointmentLocationUtils';
import { formatTimeSAST, formatLocalISOSAST, formatDateTimeSAST, getTimePartsSAST, SAST_TIMEZONE } from '@/lib/sastTime';
import useKeyboardSelectableList from '@/hooks/useKeyboardSelectableList';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';
import ModalFooter from '@/components/shared/ModalFooter';
import UserDocsLink from '@/components/shared/UserDocsLink';
import WorkItemDetailModal from '@/components/WorkItemDetailModal';
import { buildStakeholderContactWorkItem } from '@/components/contacts/stakeholderWorkItemAdapter';
import { uiViewportHeight, uiViewportWidth } from '@/lib/uiScale';

const APPOINTMENT_CREATE_LOADING_OVERLAY_Z = 20;

// ============================================================================
// TYPES & CONSTANTS
// ============================================================================

const APPOINTMENT_PRIORITIES = [
  { value: 'low', label: 'Low', icon: '○' },
  { value: 'medium', label: 'Medium', icon: '●' },
  { value: 'high', label: 'High', icon: '▲' },
  { value: 'urgent', label: 'Urgent', icon: '🔴' },
];

const CONTACT_LOOKUP_REQUEST_TIMEOUT_MS = 15_000;
const CONTACT_LOCATION_LOAD_ERROR_MESSAGE = 'We could not load locations for this contact right now.';
const CONTACT_LOCATION_LOAD_TIMEOUT_MESSAGE = 'Loading locations took too long. Please retry or use a custom location.';

type JobCustomFields = Record<string, unknown> & {
  require_exact_location?: boolean;
  require_location_verification?: boolean;
  geofence_radius_meters?: number;
  additional_notes?: string;
  equipment_required?: string;
  safety_requirements?: string;
  purchase_items?: unknown;
};

type AppointmentAvailabilityRecord = {
  id: string;
  title: string;
  scheduled_start?: string | null;
  scheduled_end?: string | null;
  status: string;
  item_number: string;
};

type AppointmentModalError = Error & {
  code?: string;
  details?: { contact_name?: string };
};

function getErrorMessage(error: unknown): string | undefined {
  return error instanceof Error ? error.message : undefined;
}

function deriveInlineJobTitle(description: string, fallbackContactName?: string | null): string {
  const normalizedDescription = description
    .split('\n')
    .map(line => line.trim())
    .find(Boolean)
    || description.trim();

  if (normalizedDescription) {
    return normalizedDescription.slice(0, 500);
  }

  return `Service job for ${fallbackContactName || 'selected contact'}`.slice(0, 500);
}

function parseJobCustomFields(value: unknown): JobCustomFields | undefined {
  if (!value) return undefined;
  if (typeof value === 'object' && !Array.isArray(value)) {
    return value as JobCustomFields;
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as JobCustomFields;
      }
    } catch {
      return undefined;
    }
  }

  return undefined;
}

interface AppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  presetJob?: AppointmentModalPresetJob | null;
  assignmentLock?: AppointmentModalAssignmentLock | null;
  // When true, modal is opened from within a job context (AppointmentsTab)
  // Customer/location/job are pre-populated and sections are compressed
  fromJobContext?: boolean;
  parentJob?: {
    id: string;
    item_number: string;
    title: string;
    description?: string | null;
    notes?: string | null;
    item_type?: string | null;
    item_subtype?: string | null;
    status?: string | null;
    module_context?: string | null;
    contact_id?: number | null;
    contact_name?: string | null;
    contact_person?: string | null;
    contact_person_id?: number | null;
    contact_person_id2?: number | null;
    contact_person_id3?: number | null;
    location_id?: number | null;
    location_name?: string | null;
    location_address?: string | null;
    location_coordinates?: string | null;
    custom_fields?: JobCustomFields;
  } | null;
  activeSolutionKey?: string | null;
  /** Workflow scope for appointment type options (e.g. 'property-management').
   *  Scoped job-config rows are preferred; falls back to the workflow template's
   *  smart_task.visit_types, then the global appointment config. */
  workflowContext?: string | null;
  /** module_context written on created appointments. Defaults to the selected
   *  parent's module_context, then 'service-operations'. */
  moduleContext?: string | null;
  /** Parent item picker config. Defaults to active jobs for a contact. */
  parentPickerConfig?: {
    /** Item types eligible as the appointment parent (default ['job']). */
    itemTypes?: string[];
    /** Optional subtype restriction for the parent search. */
    itemSubtypes?: string[];
    /** Singular label for the parent ("Job", "Unit / Tenancy"). */
    label?: string;
    /** Plural label used in list/empty states ("jobs", "units or tenancies"). */
    labelPlural?: string;
    /** Whether the inline "create parent" mini-form is offered (job-only). */
    allowCreate?: boolean;
  } | null;
  // When true, render inline instead of a document.body portal so the modal can be
  // nested inside other fixed-position containers (e.g. the ServiceOperationsModal).
  disablePortal?: boolean;
}

interface Contact {
  id: number;
  company_name: string | null;
  contact_person: string | null;
  email: string | null;
  phone: string | null;
  contact_type: string;
  account_status?: string | null;
  is_active?: boolean | null;
  payment_terms?: string | number | null;
  payment_terms_days?: string | number | null;
  credit_limit?: number | string | null;
  current_balance?: number | string | null;
  currency?: string | null;
}

interface Location {
  id: number;
  contact_id: number;
  location_name: string;
  address_line1: string | null;
  city: string | null;
  is_primary: boolean;
  latitude?: number | null;
  longitude?: number | null;
  coordinates?: unknown;
}

interface ContactPerson {
  id: number;
  contact_id: number;
  first_name: string;
  last_name: string;
  job_title: string | null;
  email: string | null;
  phone: string | null;
  is_primary?: boolean;
}

interface UnifiedSearchResult {
  type: 'contact' | 'location' | 'person';
  id: number;
  contact_id: number;
  contact_name: string;
  contact_company_name: string | null;
  contact_type?: string;
  location_id: number | null;
  location_name: string | null;
  location_address: string | null;
  location_city: string | null;
  location_latitude?: number | null;
  location_longitude?: number | null;
  person_id: number | null;
  person_name: string | null;
  person_email: string | null;
  person_job_title?: string | null;
  person_phone?: string | null;
  display_title?: string;
  display_subtitle?: string;
}

interface JobSearchResult {
  id: string;
  item_number: string;
  title: string;
  description?: string | null;
  notes?: string | null;
  status: string;
  item_type?: string | null;
  item_subtype?: string | null;
  module_context?: string | null;
  contact_id?: number | null;
  contact_name: string | null;
  contact_person?: string | null;
  contact_person_id?: number | null;
  contact_person_id2?: number | null;
  contact_person_id3?: number | null;
  location_id: number | null;
  location_name: string | null;
  location_address: string | null;
  location_coordinates?: string | null; // "(lat,lng)" point format from PostgreSQL
  manager_id?: number | null; // Job manager — inherited by the appointment at creation
  manager_name?: string | null;
  custom_fields?: JobCustomFields;
}

interface AppointmentModalPresetJob extends JobSearchResult {
  contact_id?: number | null;
}

interface AppointmentModalAssignmentLock {
  userId: number;
  displayName?: string | null;
  email?: string | null;
}

interface TeamMember {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string;
  name?: string;
  assignee_type?: 'user' | 'contractor';
}

// Existing appointment for availability display
interface ExistingAppointment {
  id: string;
  title: string;
  scheduled_start: string;
  scheduled_end: string;
  status: string;
  item_number: string;
}

function normalizePositiveIntegerId(value: number | string | null | undefined): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    const parsed = Number.parseInt(trimmed, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  return null;
}

function resolveSelectedLocationFromList(nextLocations: Location[], previousLocation: Location | null): Location | null {
  if (previousLocation?.id != null) {
    const matched = nextLocations.find((location) => Number(location.id) === Number(previousLocation.id));
    if (matched) {
      if (!hasContactLocationCoordinates(matched) && hasContactLocationCoordinates(previousLocation)) {
        return {
          ...matched,
          latitude: matched.latitude ?? previousLocation.latitude ?? null,
          longitude: matched.longitude ?? previousLocation.longitude ?? null,
          coordinates: matched.coordinates ?? previousLocation.coordinates ?? null,
        };
      }

      return matched;
    }
  }

  // Auto-select primary location (no coordinates requirement, matching ServiceJobModal)
  const primary = nextLocations.find((location) => location.is_primary);
  if (primary) {
    return primary;
  }

  // Auto-select if only one location
  if (nextLocations.length === 1) {
    return nextLocations[0];
  }

  return null;
}

function buildSyntheticLocationFromJob(job: AppointmentModalPresetJob): Location | null {
  const contactId = normalizePositiveIntegerId(job.contact_id);
  const locationId = normalizePositiveIntegerId(job.location_id);

  if (!contactId || !locationId) {
    return null;
  }

  const coordinates = extractCoordinates(job.location_coordinates);

  return {
    id: locationId,
    contact_id: contactId,
    location_name: job.location_name || 'Job location',
    address_line1: job.location_address || null,
    city: null,
    is_primary: false,
    latitude: coordinates?.lat ?? null,
    longitude: coordinates?.lng ?? null,
    coordinates: coordinates ?? job.location_coordinates ?? null,
  };
}

function buildCustomLocationFromJob(job: AppointmentModalPresetJob): CustomLocationData | null {
  if (normalizePositiveIntegerId(job.location_id) || !job.location_name) {
    return null;
  }

  const coordinates = extractCoordinates(job.location_coordinates);

  return {
    name: job.location_name,
    address: job.location_address || '',
    lat: coordinates ? String(coordinates.lat) : '',
    lng: coordinates ? String(coordinates.lng) : '',
  };
}

function buildLocationLoadErrorMessage(error: unknown): string {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return CONTACT_LOCATION_LOAD_TIMEOUT_MESSAGE;
  }

  if (error instanceof Error && /abort|timeout/i.test(error.message)) {
    return CONTACT_LOCATION_LOAD_TIMEOUT_MESSAGE;
  }

  return CONTACT_LOCATION_LOAD_ERROR_MESSAGE;
}

async function requestContactLocations(contactId: number, token: string): Promise<{ locations: Location[]; errorMessage: string | null }> {
  if (!token) {
    return {
      locations: [],
      errorMessage: CONTACT_LOCATION_LOAD_ERROR_MESSAGE,
    };
  }

  const abortController = new AbortController();
  const timeoutId = setTimeout(() => abortController.abort(), CONTACT_LOOKUP_REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(`/api/v1/contacts/${contactId}/locations`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: abortController.signal,
    });

    if (!response.ok) {
      return {
        locations: [],
        errorMessage: CONTACT_LOCATION_LOAD_ERROR_MESSAGE,
      };
    }

    const payload = await response.json().catch(() => null) as { success?: boolean; data?: Location[] } | null;
    if (payload?.success === false) {
      return {
        locations: [],
        errorMessage: CONTACT_LOCATION_LOAD_ERROR_MESSAGE,
      };
    }

    return {
      locations: Array.isArray(payload?.data) ? payload.data : [],
      errorMessage: null,
    };
  } catch (error) {
    return {
      locations: [],
      errorMessage: buildLocationLoadErrorMessage(error),
    };
  } finally {
    clearTimeout(timeoutId);
  }
}

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

// Click-based Time Picker — hour & minute wheel selectors
function ClickTimePicker({
  value,
  onChange,
  isDark,
  label,
  className = ''
}: {
  value: string; // "HH:MM"
  onChange: (v: string) => void;
  isDark: boolean;
  label?: string;
  className?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<'hour' | 'minute'>('hour');
  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const [hour, minute] = (value || '09:00').split(':').map(Number);

  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const dropdownHeight = 280;
    const dropdownWidth = Math.max(rect.width, 240);
    const maxLeft = Math.max(12, uiViewportWidth() - dropdownWidth - 12);
    const openUp = uiViewportHeight() - rect.bottom < dropdownHeight && rect.top > dropdownHeight;

    setDropdownPos({
      top: openUp ? Math.max(12, rect.top - dropdownHeight - 4) : rect.bottom + 4,
      left: Math.min(Math.max(12, rect.left), maxLeft),
    });
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        const portalEl = document.getElementById('appointment-time-picker-portal');
        if (portalEl && portalEl.contains(e.target as Node)) return;
        setIsOpen(false);
        setMode('hour');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    updatePosition();
    const handleUpdate = () => updatePosition();
    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [isOpen, updatePosition]);

  const handleHourSelect = (h: number) => {
    onChange(`${String(h).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);
    setMode('minute');
  };

  const handleMinuteSelect = (m: number) => {
    onChange(`${String(hour).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    setIsOpen(false);
    setMode('hour');
  };

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

  const displayValue = value
    ? `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
    : '--:--';

  const dropdownContent = isOpen && typeof document !== 'undefined'
    ? createPortal(
      <div
        id="appointment-time-picker-portal"
        className={`fixed z-[9999] rounded-xl shadow-xl border overflow-hidden ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}
        style={{ top: dropdownPos.top, left: dropdownPos.left, minWidth: '240px' }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={`flex border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            type="button"
            onClick={() => setMode('hour')}
            className={`flex-1 py-2 text-xs font-semibold tracking-wide uppercase transition-colors ${mode === 'hour'
              ? 'text-green-500 border-b-2 border-green-500'
              : isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            Hour
          </button>
          <button
            type="button"
            onClick={() => setMode('minute')}
            className={`flex-1 py-2 text-xs font-semibold tracking-wide uppercase transition-colors ${mode === 'minute'
              ? 'text-green-500 border-b-2 border-green-500'
              : isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            Min
          </button>
        </div>

        <div className={`px-3 py-2 text-center border-b ${isDark ? 'border-gray-700' : 'bg-gray-50 border-gray-200'
          }`}>
          <span className={`text-lg font-mono font-bold ${isDark ? 'text-gray-100' : 'text-gray-800'
            }`}>
            <span className={mode === 'hour' ? 'text-green-500' : ''}>{String(hour).padStart(2, '0')}</span>
            <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>:</span>
            <span className={mode === 'minute' ? 'text-green-500' : ''}>{String(minute).padStart(2, '0')}</span>
          </span>
        </div>

        <div className="p-2">
          {mode === 'hour' ? (
            <div className="grid grid-cols-6 gap-1">
              {hours.map(h => (
                <button
                  key={h}
                  type="button"
                  onClick={() => handleHourSelect(h)}
                  className={`py-1.5 rounded-md text-sm font-medium transition-all ${h === hour
                    ? 'bg-green-500 text-white shadow-sm'
                    : isDark
                      ? 'text-gray-300 hover:bg-gray-700'
                      : 'text-gray-700 hover:bg-gray-100'
                    }`}
                >
                  {String(h).padStart(2, '0')}
                </button>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-1">
              {minutes.map(m => (
                <button
                  key={m}
                  type="button"
                  onClick={() => handleMinuteSelect(m)}
                  className={`py-2 rounded-md text-sm font-medium transition-all ${m === minute
                    ? 'bg-green-500 text-white shadow-sm'
                    : isDark
                      ? 'text-gray-300 hover:bg-gray-700'
                      : 'text-gray-700 hover:bg-gray-100'
                    }`}
                >
                  :{String(m).padStart(2, '0')}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>,
      document.body
    )
    : null;

  return (
    <div className={`relative ${className}`} ref={ref}>
      {label && (
        <span className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{label}</span>
      )}
      <button
        ref={buttonRef}
        type="button"
        onClick={() => {
          setIsOpen(!isOpen);
          setMode('hour');
        }}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-mono transition-colors w-full justify-between ${isDark
          ? 'bg-gray-700 border-gray-600 text-gray-200 hover:border-gray-500'
          : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
          } focus:ring-2 focus:ring-green-500 focus:border-green-500`}
      >
        <div className="flex items-center gap-2">
          <Clock className="w-3.5 h-3.5 text-gray-400" />
          <span>{displayValue}</span>
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {dropdownContent}
    </div>
  );
}

// Multi-select dropdown for team members with availability display
function TeamMemberMultiSelect({
  selectedIds,
  teamMembers,
  onToggle,
  loading,
  isDark,
  availability,
  loadingAvailability,
  onDropdownOpen,
}: {
  selectedIds: number[];
  teamMembers: TeamMember[];
  onToggle: (id: number) => void;
  loading: boolean;
  isDark: boolean;
  availability?: Record<number, ExistingAppointment[]>;
  loadingAvailability?: boolean;
  onDropdownOpen?: () => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredMembers = teamMembers.filter(m => {
    const name = `${m.first_name || ''} ${m.last_name || ''} ${m.email || ''}`.toLowerCase();
    return name.includes(search.toLowerCase());
  });

  const selectedMembers = teamMembers.filter(m => selectedIds.includes(m.id));

  const formatTime = (dateStr: string) => formatTimeSAST(dateStr);

  const handleDropdownToggle = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    if (newState && onDropdownOpen) {
      onDropdownOpen();
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Selected member cards with availability */}
      {selectedMembers.length > 0 && (
        <div className="space-y-2 mb-3">
          {selectedMembers.map(member => {
            const memberName = `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email?.split('@')[0] || 'User';
            const memberAppts = availability?.[member.id] || [];
            return (
              <div key={member.id} className={`rounded-lg border p-3 ${isDark ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${isDark ? 'bg-blue-800 text-blue-200' : 'bg-blue-200 text-blue-800'}`}>
                      {(member.first_name?.[0] || '')} {(member.last_name?.[0] || '')}
                    </div>
                    <div>
                      <div className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{memberName}</div>
                      <div className="text-xs text-gray-500">{member.email}</div>
                    </div>
                  </div>
                  <button type="button" onClick={() => onToggle(member.id)} className="p-1 rounded hover:bg-black/10"><X className="w-3.5 h-3.5 text-gray-400" /></button>
                </div>
                {/* Availability */}
                {loadingAvailability ? (
                  <div className="mt-2 flex items-center gap-1 text-xs text-gray-500"><Loader2 className="w-3 h-3 animate-spin" />Loading availability...</div>
                ) : memberAppts.length > 0 ? (
                  <div className="mt-2">
                    <div className={`text-xs font-medium mb-1 flex items-center gap-1 ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                      <AlertTriangle className="w-3 h-3" />
                      Existing bookings on this date:
                    </div>
                    <div className="space-y-1">
                      {memberAppts.map(apt => (
                        <div key={apt.id} className={`text-xs px-2 py-1 rounded flex items-center gap-2 ${isDark ? 'bg-amber-900/30 text-amber-300' : 'bg-amber-50 text-amber-800'}`}>
                          <Clock className="w-3 h-3 flex-shrink-0" />
                          <span className="font-medium">{formatTime(apt.scheduled_start)} - {formatTime(apt.scheduled_end)}</span>
                          <span className="truncate">{apt.item_number}: {apt.title}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className={`mt-2 text-xs flex items-center gap-1 ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                    <Check className="w-3 h-3" />No existing bookings on this date
                  </div>
                )}
              </div>
            );
          })}
          {selectedMembers.length > 1 && (
            <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs ${isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>
              <Info className="w-3.5 h-3.5 flex-shrink-0" />
              All selected team members will be assigned to the same shared appointment.
            </div>
          )}
        </div>
      )}

      {/* Dropdown trigger */}
      <button
        type="button"
        onClick={handleDropdownToggle}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border transition-colors ${isDark
          ? 'bg-gray-700 border-gray-600 text-gray-200 hover:border-gray-500'
          : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
          }`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Users className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <span className={`truncate ${selectedIds.length === 0 ? 'text-gray-400' : ''}`}>
            {selectedIds.length === 0 ? 'Select team member(s)...' : `${selectedIds.length} selected for one shared appointment`}
          </span>
        </div>
        <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className={`absolute z-50 w-full mt-1 rounded-lg shadow-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
          {/* Search */}
          <div className={`p-2 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search team members..."
                className={`w-full pl-8 pr-3 py-1.5 text-sm rounded border ${isDark
                  ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-400'
                  : 'bg-gray-50 border-gray-200 text-gray-700 placeholder-gray-400'
                  }`}
                autoFocus
              />
            </div>
          </div>

          {/* Options */}
          <div className="max-h-48 overflow-y-auto">
            {loading ? (
              <div className="p-3 text-center text-gray-500 text-sm">
                <Loader2 className="w-4 h-4 animate-spin mx-auto mb-1" />
                Loading...
              </div>
            ) : filteredMembers.length === 0 ? (
              <div className="p-3 text-center text-gray-500 text-sm">
                No team members found
              </div>
            ) : (
              filteredMembers.map(member => {
                const isSelected = selectedIds.includes(member.id);
                const isContractor = member.assignee_type === 'contractor' || member.id < 0;
                return (
                  <button
                    key={member.id}
                    type="button"
                    onClick={() => onToggle(member.id)}
                    className={`w-full flex items-center gap-2 p-2 text-left transition-colors ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                      } ${isSelected ? (isDark ? 'bg-gray-700' : 'bg-blue-50') : ''}`}
                  >
                    <div className={`w-5 h-5 rounded border flex items-center justify-center ${isSelected
                      ? 'bg-blue-500 border-blue-500 text-white'
                      : isDark ? 'border-gray-500' : 'border-gray-300'
                      }`}>
                      {isSelected && <Check className="w-3 h-3" />}
                    </div>
                    {isContractor && (
                      <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${isDark ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-700'}`}>
                        <Briefcase className="w-3 h-3" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        {member.first_name || ''} {member.last_name || (member.email?.split('@')[0] ?? 'User')}
                        {isContractor && (
                          <span className={`ml-1.5 text-xs font-normal ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>(Contractor)</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 truncate">{member.email || ''}</div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function OptionalAccordionSection({
  title,
  description,
  icon,
  isOpen,
  onToggle,
  isDark,
  children,
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  isDark: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={`overflow-hidden rounded-xl border ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50/80'}`}>
      <button
        type="button"
        onClick={onToggle}
        className={`flex w-full items-center justify-between gap-4 px-4 py-4 text-left transition-colors ${isDark ? 'hover:bg-gray-800/70' : 'hover:bg-white/80'
          }`}
        aria-expanded={isOpen}
      >
        <div className="flex min-w-0 items-start gap-3">
          <div className={`mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${isDark ? 'bg-gray-700 text-gray-200' : 'bg-white text-gray-700 shadow-sm'}`}>
            {icon}
          </div>
          <div className="min-w-0">
            <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>{title}</h3>
            <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{description}</p>
          </div>
        </div>
        <ChevronDown className={`h-4 w-4 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''} ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
      </button>

      {isOpen && (
        <div className={`border-t px-4 pb-4 pt-4 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          {children}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// DAY SCHEDULE TIMELINE
// Visual timeline showing a team member's schedule for the selected day
// ============================================================================

const TIMELINE_START_HOUR = 6;  // 6 AM
const TIMELINE_END_HOUR = 20;   // 8 PM
const TIMELINE_HOURS = TIMELINE_END_HOUR - TIMELINE_START_HOUR; // 14 hours

function DayScheduleTimeline({
  memberName,
  appointments,
  proposedStart,
  proposedEnd,
  isDark,
}: {
  memberName: string;
  appointments: ExistingAppointment[];
  proposedStart: string;
  proposedEnd: string;
  isDark: boolean;
}) {
  const toMinutesFromStart = (dateStr: string) => {
    const { hours, minutes } = getTimePartsSAST(dateStr);
    const totalMin = hours * 60 + minutes;
    return Math.max(0, Math.min(totalMin - TIMELINE_START_HOUR * 60, TIMELINE_HOURS * 60));
  };

  const pct = (minutes: number) => (minutes / (TIMELINE_HOURS * 60)) * 100;

  const formatTime = (dateStr: string) => formatTimeSAST(dateStr);

  // Sort existing appointments by start time
  const sorted = [...appointments].sort(
    (a, b) => new Date(a.scheduled_start).getTime() - new Date(b.scheduled_start).getTime()
  );

  // Proposed slot position
  const hasProposed = proposedStart && proposedEnd;
  const proposedLeftPct = hasProposed ? pct(toMinutesFromStart(proposedStart)) : 0;
  const proposedWidthPct = hasProposed ? pct(toMinutesFromStart(proposedEnd) - toMinutesFromStart(proposedStart)) : 0;

  // Check for conflicts
  const hasConflict = hasProposed && findAppointmentClashes(sorted, {
    newStart: new Date(proposedStart),
    newEnd: new Date(proposedEnd),
    excludeHiddenStatuses: true,
  }).length > 0;

  // Hour markers
  const hourMarkers = [];
  for (let h = TIMELINE_START_HOUR; h <= TIMELINE_END_HOUR; h += 2) {
    hourMarkers.push(h);
  }

  return (
    <div className="mb-3 last:mb-0">
      {/* Member name */}
      <div className={`text-xs font-medium mb-1 flex items-center gap-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
        <span>{memberName}</span>
        {hasConflict && (
          <span className="flex items-center gap-1 text-red-500">
            <AlertTriangle className="w-3 h-3" />
            <span className="font-semibold">Conflict</span>
          </span>
        )}
        {!hasConflict && sorted.length === 0 && (
          <span className={`flex items-center gap-1 ${isDark ? 'text-green-400' : 'text-green-600'}`}>
            <Check className="w-3 h-3" />Free
          </span>
        )}
      </div>

      {/* Timeline bar */}
      <div className={`relative h-10 rounded-lg overflow-hidden ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
        {/* Hour gridlines */}
        {hourMarkers.map(h => (
          <div
            key={h}
            className={`absolute top-0 bottom-0 border-l ${isDark ? 'border-gray-600' : 'border-gray-200'}`}
            style={{ left: `${pct((h - TIMELINE_START_HOUR) * 60)}%` }}
          />
        ))}

        {/* Existing appointments */}
        {sorted.map(apt => {
          const left = pct(toMinutesFromStart(apt.scheduled_start));
          const width = Math.max(pct(toMinutesFromStart(apt.scheduled_end) - toMinutesFromStart(apt.scheduled_start)), 1);
          return (
            <div
              key={apt.id}
              className={`absolute top-1 bottom-1 rounded-md flex items-center justify-center overflow-hidden cursor-default group ${isDark ? 'bg-amber-700/70 border border-amber-600' : 'bg-amber-200 border border-amber-300'
                }`}
              style={{ left: `${left}%`, width: `${width}%` }}
              title={`${apt.item_number}: ${apt.title}\n${formatTime(apt.scheduled_start)} – ${formatTime(apt.scheduled_end)}`}
            >
              <span className={`text-[10px] font-medium truncate px-1 ${isDark ? 'text-amber-100' : 'text-amber-900'}`}>
                {formatTime(apt.scheduled_start)}-{formatTime(apt.scheduled_end)}
              </span>
            </div>
          );
        })}

        {/* Proposed new appointment */}
        {hasProposed && proposedWidthPct > 0 && (
          <div
            className={`absolute top-0 bottom-0 rounded-md border-2 border-dashed ${hasConflict
              ? 'bg-red-500/20 border-red-500'
              : isDark ? 'bg-green-500/25 border-green-400' : 'bg-green-500/20 border-green-500'
              }`}
            style={{ left: `${proposedLeftPct}%`, width: `${proposedWidthPct}%` }}
            title={`New: ${formatTime(proposedStart)} – ${formatTime(proposedEnd)}`}
          >
            <span className={`absolute inset-0 flex items-center justify-center text-[10px] font-bold ${hasConflict ? 'text-red-600' : isDark ? 'text-green-300' : 'text-green-700'
              }`}>
              NEW
            </span>
          </div>
        )}
      </div>

      {/* Hour labels */}
      <div className="relative h-4 mt-0.5">
        {hourMarkers.map(h => (
          <span
            key={h}
            className={`absolute text-[10px] -translate-x-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}
            style={{ left: `${pct((h - TIMELINE_START_HOUR) * 60)}%` }}
          >
            {String(h).padStart(2, '0')}:00
          </span>
        ))}
      </div>

      {/* Appointment list under timeline */}
      {sorted.length > 0 && (
        <div className="mt-1 space-y-0.5">
          {sorted.map(apt => (
            <div key={apt.id} className={`text-[11px] flex items-center gap-1.5 px-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              <span className={`font-medium ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
                {formatTime(apt.scheduled_start)}–{formatTime(apt.scheduled_end)}
              </span>
              <span className="truncate">{apt.item_number}: {apt.title}</span>
              <span className={`text-[10px] px-1 rounded ${apt.status === 'in_progress' ? (isDark ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-700')
                : apt.status === 'completed' ? (isDark ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-700')
                  : isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-600'
                }`}>{apt.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function AppointmentModal({
  isOpen,
  onClose,
  onSuccess,
  presetJob = null,
  assignmentLock = null,
  fromJobContext = false,
  parentJob = null,
  activeSolutionKey,
  workflowContext = null,
  moduleContext = null,
  parentPickerConfig = null,
  disablePortal = false,
}: AppointmentModalProps) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme?.mode === 'dark';
  const {
    users: assignmentUsers,
    teams: operationsTeams,
    loading: loadingAssignmentOptions,
    getTeamById,
    getTeamMemberUserIds,
    contractors: assignmentContractors,
  } = useOperationsAssignmentOptions({ activeSolutionKey });

  // Dynamic config from settings — workflow-scoped rows are preferred when a
  // workflowContext is provided (e.g. property-management appointment/visit types).
  const { jobTypes: configAppointmentTypes, priorities: configPriorities } = useJobConfig({
    itemType: 'appointment',
    workflowContext: workflowContext || undefined,
  });
  const [workflowVisitTypes, setWorkflowVisitTypes] = useState<ConfigurableTypeOption[] | null>(null);

  // When a workflowContext is provided, pull the workflow template's
  // smart_task.visit_types — the workflow-declared appointment taxonomy
  // (e.g. property visit types: inspection, move_in, move_out, ...).
  useEffect(() => {
    let cancelled = false;
    if (!workflowContext || !isOpen) {
      setWorkflowVisitTypes(null);
      return;
    }
    void fetchWorkflowTemplate(workflowContext).then((template) => {
      if (cancelled) return;
      const rawVisitTypes = (template?.config?.smart_task as Record<string, unknown> | undefined)?.visit_types;
      if (!Array.isArray(rawVisitTypes)) {
        setWorkflowVisitTypes(null);
        return;
      }
      const options = rawVisitTypes
        .map((entry) => {
          const record = entry as Record<string, unknown> | null;
          const key = typeof record?.key === 'string' ? record.key.trim() : '';
          const label = typeof record?.label === 'string' ? record.label.trim() : '';
          if (!key || !label) return null;
          return {
            key,
            label,
            icon: typeof record?.icon === 'string' ? record.icon : undefined,
            color: typeof record?.color === 'string' ? record.color : undefined,
          } as ConfigurableTypeOption;
        })
        .filter((entry): entry is ConfigurableTypeOption => Boolean(entry));
      setWorkflowVisitTypes(options.length > 0 ? options : null);
    });
    return () => { cancelled = true; };
  }, [workflowContext, isOpen]);

  const appointmentTypeOptions = useMemo(
    () => (workflowVisitTypes?.length
      ? workflowVisitTypes
      : configAppointmentTypes.length > 0 ? configAppointmentTypes : DEFAULT_APPOINTMENT_TYPES),
    [workflowVisitTypes, configAppointmentTypes]
  );

  // Parent picker configuration — defaults to the service-job picker.
  const parentItemTypes = useMemo(
    () => (parentPickerConfig?.itemTypes?.length ? parentPickerConfig.itemTypes : ['job']),
    [parentPickerConfig]
  );
  const parentItemSubtypes = parentPickerConfig?.itemSubtypes?.length ? parentPickerConfig.itemSubtypes : null;
  const parentLabel = parentPickerConfig?.label || 'Job';
  const parentLabelPlural = parentPickerConfig?.labelPlural || 'jobs';
  const allowParentCreate = parentPickerConfig?.allowCreate ?? parentItemTypes.every((type) => type === 'job');

  // Filter users to only include team members when activeSolutionKey is provided
  const filteredAssignmentUsers = useMemo(() => {
    if (!activeSolutionKey) {
      return assignmentUsers;
    }
    const teamMemberUserIds = getTeamMemberUserIds();
    return assignmentUsers.filter((user) => teamMemberUserIds.includes(user.id));
  }, [assignmentUsers, activeSolutionKey, getTeamMemberUserIds]);

  const getDefaultAppointmentSubtype = useCallback(
    () => appointmentTypeOptions[0]?.key || DEFAULT_APPOINTMENT_TYPES[0]?.key || 'site_visit',
    [appointmentTypeOptions]
  );

  const defaults = getDefaultAppointmentSchedule();
  const lockedAssigneeUserId = normalizePositiveIntegerId(assignmentLock?.userId);
  const isAssignmentLockedToCurrentUser = lockedAssigneeUserId != null;
  const buildInitialFormData = useCallback(() => ({
    title: '',
    description: '',
    additional_notes: '',
    status: 'scheduled',
    priority: '',
    item_subtype: '',
    scheduled_start: defaults.scheduled_start,
    scheduled_end: defaults.scheduled_end,
    assigned_team: null as string | null,
    assigned_to: lockedAssigneeUserId ? [lockedAssigneeUserId] : [] as number[],
    require_location_verification: false,
    geofence_radius_meters: 100,
    // Equipment & Safety (inherited from parent job, editable per appointment)
    equipment_required: '',
    safety_requirements: '',
    purchase_items: [] as PurchaseListItem[],
    // Contact person for custom location
    custom_contact_person: '',
    // Whether to copy the parent job's extra contact people (slots 2-3)
    inherit_all_contact_people: true,
  }), [defaults.scheduled_end, defaults.scheduled_start, lockedAssigneeUserId]);

  // Form state
  const [formData, setFormData] = useState(buildInitialFormData);
  const purchaseItemListRef = useRef<PurchaseItemListHandle | null>(null);

  // Purchase Order multiselect state (for expense-required appointments with supplier contact)
  const [selectedPurchaseOrders, setSelectedPurchaseOrders] = useState<Array<{ id: string; item_number: string; title: string; contact_name: string }>>([]);
  const [poSearchQuery, setPoSearchQuery] = useState('');
  const [poSearchResults, setPoSearchResults] = useState<Array<{ id: string; item_number: string; title: string; contact_name: string }>>([]);
  const [isSearchingPO, setIsSearchingPO] = useState(false);
  const [showPOSearch, setShowPOSearch] = useState(false);
  const poDropdownRef = useRef<HTMLDivElement>(null);

  // Contact state
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [loadingContacts, setLoadingContacts] = useState(false);

  // Location state
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [locationLoadError, setLocationLoadError] = useState<string | null>(null);
  const [showLocationCreate, setShowLocationCreate] = useState(false);

  // Contact Person state
  const [contactPeople, setContactPeople] = useState<ContactPerson[]>([]);
  const [selectedPerson, setSelectedPerson] = useState<ContactPerson | null>(null);
  const [loadingPeople, setLoadingPeople] = useState(false);

  // Stakeholder overview state (view-only; no onboarding since appointments require existing contacts)
  const [showStakeholderOverview, setShowStakeholderOverview] = useState(false);

  // Job state
  const [jobSearchQuery, setJobSearchQuery] = useState('');
  const [availableJobs, setAvailableJobs] = useState<JobSearchResult[]>([]);
  const [filteredJobs, setFilteredJobs] = useState<JobSearchResult[]>([]);
  const [selectedJob, setSelectedJob] = useState<JobSearchResult | null>(null) as [JobSearchResult | null, (job: JobSearchResult | null) => void];
  const [searchingJobs, setSearchingJobs] = useState(false);
  const [showJobCreateForm, setShowJobCreateForm] = useState(false);
  const [newJobDescription, setNewJobDescription] = useState('');
  const [creatingJob, setCreatingJob] = useState(false);
  const [isJobDropdownOpen, setIsJobDropdownOpen] = useState(false);
  const jobDropdownRef = useRef<HTMLDivElement>(null);

  const technicians = useMemo<TeamMember[]>(() => {
    const users = filteredAssignmentUsers.map((user) => ({
      id: user.id,
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email,
    }));
    // Add contractors with negative IDs to distinguish from users
    const contractors = assignmentContractors.map((c) => ({
      id: -c.id,
      first_name: c.company_name || c.contact_person || null,
      last_name: c.company_name ? '(Contractor)' : null,
      email: c.email || '',
      name: c.company_name || c.contact_person || `Contractor #${c.id}`,
      assignee_type: 'contractor' as const,
    }));
    return [...users, ...contractors];
  }, [filteredAssignmentUsers, assignmentContractors]);
  const lockedAssignee = useMemo<TeamMember | null>(() => {
    if (!lockedAssigneeUserId) {
      return null;
    }

    const matchedTechnician = technicians.find((member) => member.id === lockedAssigneeUserId);
    if (matchedTechnician) {
      return matchedTechnician;
    }

    return {
      id: lockedAssigneeUserId,
      first_name: assignmentLock?.displayName || null,
      last_name: null,
      email: assignmentLock?.email || '',
      name: assignmentLock?.displayName || assignmentLock?.email || `User #${lockedAssigneeUserId}`,
    };
  }, [assignmentLock?.displayName, assignmentLock?.email, lockedAssigneeUserId, technicians]);
  const effectiveTechnicians = useMemo(() => {
    if (!lockedAssignee) {
      return technicians;
    }

    return [
      lockedAssignee,
      ...technicians.filter((member) => member.id !== lockedAssignee.id),
    ];
  }, [lockedAssignee, technicians]);
  const loadingTechnicians = loadingAssignmentOptions;

  // Team member availability
  const [availability, setAvailability] = useState<Record<number, ExistingAppointment[]>>({});
  const [loadingAvailability, setLoadingAvailability] = useState(false);

  // Location source: 'contact' = from selected contact's locations, 'custom' = Google Places (work_item only)
  const [locationSource, setLocationSource] = useState<'contact' | 'custom'>('contact');
  const [customLocation, setCustomLocation] = useState<CustomLocationData | null>(null);
  const [customLocSearch, setCustomLocSearch] = useState('');

  // UI state
  const [creating, setCreating] = useState(false);
  const [expandedRequirements, setExpandedRequirements] = useState(false);
  const [recurrenceRule, setRecurrenceRule] = useState<AppointmentRecurrenceRule>({
    enabled: false,
    frequency: 'weekly',
    intervalCount: 1,
    occurrenceCount: 4,
  });
  // Track if we've finished loading jobs for current contact (to distinguish loading vs no-results)
  const [jobsChecked, setJobsChecked] = useState(false);

  const selectedContactId = useMemo(
    () => normalizePositiveIntegerId(selectedContact?.id),
    [selectedContact?.id]
  );

  useEffect(() => {
    if (appointmentTypeOptions.length === 0) return;

    setFormData(prev => {
      if (!prev.item_subtype) {
        return prev;
      }

      if (appointmentTypeOptions.some(type => type.key === prev.item_subtype)) {
        return prev;
      }

      return {
        ...prev,
        item_subtype: '',
      };
    });
  }, [appointmentTypeOptions]);

  // Refs for click outside handling
  const activeContactIdRef = useRef<number | null>(null);
  const latestLocationLoadRequestId = useRef(0);
  const latestPeopleLoadRequestId = useRef(0);
  const latestJobLoadRequestId = useRef(0);
  const appliedPresetJobIdRef = useRef<string | null>(null);
  const contactLocationsCacheRef = useRef(new Map<number, Location[]>());
  const contactJobsCacheRef = useRef(new Map<number, JobSearchResult[]>());

  const getAuthToken = useCallback(() => authService.getTokens()?.access_token || '', []);

  useEffect(() => {
    activeContactIdRef.current = selectedContactId;
  }, [selectedContactId]);

  // Click outside handler for job dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (jobDropdownRef.current && !jobDropdownRef.current.contains(e.target as Node)) {
        setIsJobDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Click outside handler for PO dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (poDropdownRef.current && !poDropdownRef.current.contains(e.target as Node)) {
        setShowPOSearch(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen || !lockedAssigneeUserId) {
      return;
    }

    setFormData((prev) => {
      if (
        prev.assigned_team === null
        && prev.assigned_to.length === 1
        && prev.assigned_to[0] === lockedAssigneeUserId
      ) {
        return prev;
      }

      return {
        ...prev,
        assigned_team: null,
        assigned_to: [lockedAssigneeUserId],
      };
    });
  }, [isOpen, lockedAssigneeUserId]);

  // Derive selected date from scheduled_start for availability lookup
  const selectedDateStr = useMemo(() => {
    if (!formData.scheduled_start) return '';
    // Extract date part from datetime-local value (YYYY-MM-DD)
    return formData.scheduled_start.split('T')[0] || '';
  }, [formData.scheduled_start]);

  const isPurchaseAppointmentType = useMemo(
    () => normalizeAppointmentTypeKey(formData.item_subtype) === PURCHASE_APPOINTMENT_TYPE_KEY,
    [formData.item_subtype]
  );

  const isExpenseRequiredAppointmentType = useMemo(() => {
    const selectedType = appointmentTypeOptions.find((t) => t.key === formData.item_subtype);
    return selectedType?.rules?.completionRequirements?.includes('expenses') ?? false;
  }, [appointmentTypeOptions, formData.item_subtype]);

  const isSupplierContact = useMemo(() => {
    const type = selectedContact?.contact_type?.toLowerCase();
    return type === 'supplier' || type === 'vendor';
  }, [selectedContact]);

  const purchaseItems = useMemo(
    () => normalizePurchaseListItems(formData.purchase_items),
    [formData.purchase_items]
  );

  const scheduleValidation = useMemo(
    () => getAppointmentScheduleValidation(formData.scheduled_start, formData.scheduled_end),
    [formData.scheduled_end, formData.scheduled_start]
  );

  const scheduleError = useMemo(
    () => getAppointmentScheduleErrorMessage(formData.scheduled_start, formData.scheduled_end),
    [formData.scheduled_end, formData.scheduled_start]
  );

  const selectedAssignmentTeam = useMemo(
    () => getTeamById(formData.assigned_team) || null,
    [formData.assigned_team, getTeamById]
  );

  const selectedAssignmentUserIds = useMemo(
    () => selectedAssignmentTeam?.memberUserIds || formData.assigned_to,
    [formData.assigned_to, selectedAssignmentTeam]
  );

  const sharedLocations = useMemo<UnifiedLookupLocation[]>(() => (
    locations.map((location) => ({
      id: location.id,
      contact_id: location.contact_id,
      location_name: location.location_name,
      address_line1: location.address_line1 || null,
      address_line2: null,
      city: location.city || null,
      state: null,
      postal_code: null,
      country: null,
      contact_person: null,
      contact_email: null,
      contact_phone: null,
      is_primary: location.is_primary,
    }))
  ), [locations]);

  const sharedContactPeople = useMemo<UnifiedLookupContactPerson[]>(() => (
    contactPeople.map((person) => ({
      id: person.id,
      contact_id: person.contact_id,
      first_name: person.first_name,
      last_name: person.last_name,
      job_title: person.job_title,
      email: person.email,
      phone: person.phone,
      mobile: null,
      is_primary: false,
      default_location_id: null,
      default_location_name: null,
      contact_company_name: selectedContact?.company_name || null,
    }))
  ), [contactPeople, selectedContact?.company_name]);

  const selectedTeamLeaderUserId = selectedAssignmentTeam?.leader?.user_id
    ?? selectedAssignmentUserIds[0]
    ?? null;

  const hasAssignmentSelection = !!selectedAssignmentTeam || selectedAssignmentUserIds.length > 0;

  const recurringOccurrences = useMemo(
    () => recurrenceRule.enabled
      ? buildRecurringAppointmentOccurrences(formData.scheduled_start, formData.scheduled_end, recurrenceRule)
      : [{ sequence: 1, scheduled_start: formData.scheduled_start, scheduled_end: formData.scheduled_end }],
    [formData.scheduled_end, formData.scheduled_start, recurrenceRule]
  );

  // ============================================================================
  // API FUNCTIONS - defined before useEffect that uses them
  // ============================================================================

  const fetchContacts = useCallback(async () => {
    setLoadingContacts(true);
    try {
      const res = await fetch('/api/v1/contacts?limit=500', {
        headers: { 'Authorization': `Bearer ${authService.getTokens()?.access_token || ''}` }
      });
      const data = await res.json();
      if (data.success) {
        setContacts(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoadingContacts(false);
    }
  }, []);

  const fetchLocationsForContact = useCallback(async (contactId: number, options: { forceRefresh?: boolean } = {}) => {
    activeContactIdRef.current = contactId;
    const requestId = ++latestLocationLoadRequestId.current;
    setLocationLoadError(null);

    if (!options.forceRefresh && contactLocationsCacheRef.current.has(contactId)) {
      const cachedLocations = contactLocationsCacheRef.current.get(contactId) || [];
      if (requestId !== latestLocationLoadRequestId.current || activeContactIdRef.current !== contactId) {
        return;
      }

      setLocations(cachedLocations);
      setSelectedLocation((prev) => resolveSelectedLocationFromList(cachedLocations, prev));
      setLoadingLocations(false);
      return;
    }

    setLoadingLocations(true);
    const result = await requestContactLocations(contactId, getAuthToken());

    if (requestId !== latestLocationLoadRequestId.current || activeContactIdRef.current !== contactId) {
      return;
    }

    if (result.errorMessage === null) {
      contactLocationsCacheRef.current.set(contactId, result.locations);
    } else {
      console.error('Error fetching locations:', result.errorMessage);
    }

    setLocations(result.locations);
    setSelectedLocation((prev) => resolveSelectedLocationFromList(result.locations, prev));
    setLocationLoadError(result.errorMessage);
    setLoadingLocations(false);
  }, [getAuthToken]);

  const fetchContactPeople = useCallback(async (contactId: number) => {
    activeContactIdRef.current = contactId;
    const requestId = ++latestPeopleLoadRequestId.current;
    setLoadingPeople(true);
    try {
      if (requestId !== latestPeopleLoadRequestId.current || activeContactIdRef.current !== contactId) {
        return;
      }
      const nextPeople = await fetchGuardedContactPeople<ContactPerson>({
        contactId,
        token: getAuthToken(),
        activeOnly: true,
      });
      if (nextPeople.length > 0) {
        setContactPeople(nextPeople);
        setSelectedPerson((prev) => {
          if (prev?.id != null) {
            const matched = nextPeople.find((person: ContactPerson) => Number(person.id) === Number(prev.id));
            if (matched) {
              return matched;
            }
          }

          // Auto-select if only one person
          if (nextPeople.length === 1) {
            return nextPeople[0];
          }

          // Auto-select primary person
          const primary = nextPeople.find((person: ContactPerson) => person.is_primary);
          if (primary) {
            return primary;
          }

          return null;
        });
      } else {
        setContactPeople([]);
        setSelectedPerson(null);
      }
    } catch (error) {
      if (requestId !== latestPeopleLoadRequestId.current || activeContactIdRef.current !== contactId) {
        return;
      }
      console.error('Error fetching contact people:', error);
      setContactPeople([]);
      setSelectedPerson(null);
    } finally {
      if (requestId === latestPeopleLoadRequestId.current && activeContactIdRef.current === contactId) {
        setLoadingPeople(false);
      }
    }
  }, [getAuthToken]);

  const fetchActiveJobs = useCallback(async (contactId: number, options: { forceRefresh?: boolean } = {}) => {
    activeContactIdRef.current = contactId;
    const requestId = ++latestJobLoadRequestId.current;

    if (!options.forceRefresh && contactJobsCacheRef.current.has(contactId)) {
      const cachedJobs = contactJobsCacheRef.current.get(contactId) || [];
      if (requestId !== latestJobLoadRequestId.current || activeContactIdRef.current !== contactId) {
        return;
      }

      setAvailableJobs(cachedJobs);
      setFilteredJobs(cachedJobs);
      setSearchingJobs(false);
      setJobsChecked(true);
      return;
    }

    setSearchingJobs(true);
    setJobsChecked(false);
    try {
      // Load active parent items for the selected contact only — jobs for the
      // default picker, or the configured parent types (units, tenancies, ...).
      const parentParams = new URLSearchParams({
        item_type: parentItemTypes.join(','),
        contact_id: String(contactId),
        limit: '200',
      });
      if (parentItemSubtypes?.length) {
        parentParams.set('item_subtype', parentItemSubtypes.join(','));
      }
      const res = await fetch(`/api/v1/work-items?${parentParams.toString()}`, {
        headers: { 'Authorization': `Bearer ${getAuthToken()}` }
      });
      if (requestId !== latestJobLoadRequestId.current || activeContactIdRef.current !== contactId) {
        return;
      }
      const data = await res.json();
      if (data.success) {
        // Filter to only active parent items — excludes terminal job statuses
        // plus property terminal states (terminated tenancies, archived units).
        const activeJobs = (data.data || []).filter((j: JobSearchResult) =>
          !['completed', 'cancelled', 'closed', 'resolved', 'terminated', 'archived'].includes(j.status)
        );
        contactJobsCacheRef.current.set(contactId, activeJobs);
        setAvailableJobs(activeJobs);
        setFilteredJobs(activeJobs);
      } else {
        setAvailableJobs([]);
        setFilteredJobs([]);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
      if (requestId !== latestJobLoadRequestId.current || activeContactIdRef.current !== contactId) {
        return;
      }
      setAvailableJobs([]);
      setFilteredJobs([]);
    } finally {
      if (requestId === latestJobLoadRequestId.current && activeContactIdRef.current === contactId) {
        setSearchingJobs(false);
        setJobsChecked(true);
      }
    }
  }, [getAuthToken, parentItemTypes, parentItemSubtypes]);

  const searchPurchaseOrders = useCallback(async (query: string, supplierId?: number) => {
    if (!query.trim() && !supplierId) {
      setPoSearchResults([]);
      return;
    }
    setIsSearchingPO(true);
    try {
      const token = authService.getTokens()?.access_token;
      const params = new URLSearchParams({ action: 'purchase-orders', status: 'open', limit: '20' });
      if (query.trim()) params.set('search', query.trim());
      if (supplierId) params.set('supplier_id', String(supplierId));
      const response = await fetch(`/api/v1/work-items?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token || ''}` },
      });
      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        setPoSearchResults(
          data.data.map((po: { id: string; item_number: string; title: string; contact_name: string }) => ({
            id: po.id,
            item_number: po.item_number,
            title: po.title || '',
            contact_name: po.contact_name || '',
          }))
        );
      } else {
        setPoSearchResults([]);
      }
    } catch (error) {
      console.warn('[AppointmentModal] Failed to search purchase orders:', error);
      setPoSearchResults([]);
    } finally {
      setIsSearchingPO(false);
    }
  }, []);

  // Debounced purchase order search for supplier contacts
  useEffect(() => {
    const timer = setTimeout(() => {
      const supplierId = selectedContact?.id;
      if (supplierId && isSupplierContact && (poSearchQuery.length >= 2 || !poSearchQuery.trim())) {
        void searchPurchaseOrders(poSearchQuery, supplierId);
      } else {
        setPoSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [poSearchQuery, selectedContact, isSupplierContact, searchPurchaseOrders]);

  // Clear PO selection when appointment type no longer requires expenses
  useEffect(() => {
    if (!isExpenseRequiredAppointmentType) {
      setSelectedPurchaseOrders([]);
      setPoSearchQuery('');
      setPoSearchResults([]);
      setShowPOSearch(false);
    }
  }, [isExpenseRequiredAppointmentType]);

  // Fetch existing appointments for selected team members on a given date
  const fetchAvailability = useCallback(async (
    userIds: number[],
    dateStr: string,
    options: { updateState?: boolean } = {}
  ): Promise<Record<number, ExistingAppointment[]>> => {
    if (!dateStr || userIds.length === 0) return {};
    setLoadingAvailability(true);
    try {
      // Build date range for the full day
      const dayStart = `${dateStr}T00:00:00`;
      const dayEnd = `${dateStr}T23:59:59`;

      const newAvailability: Record<number, ExistingAppointment[]> = {};

      // Fetch appointments for each user in parallel
      await Promise.all(userIds.map(async (userId) => {
        try {
          const res = await fetch(
            `/api/v1/work-items?item_type=appointment&assigned_to=${userId}&scheduled_start_from=${dayStart}&scheduled_start_to=${dayEnd}&limit=50`,
            { headers: { 'Authorization': `Bearer ${getAuthToken()}` } }
          );
          const data = await res.json();
          if (data.success) {
            newAvailability[userId] = (data.data || [])
              .filter((apt: AppointmentAvailabilityRecord) => apt.scheduled_start && apt.scheduled_end && !isScheduleHiddenAppointmentStatus(apt.status))
              .map((apt: AppointmentAvailabilityRecord) => ({
                id: apt.id,
                title: apt.title,
                scheduled_start: apt.scheduled_start,
                scheduled_end: apt.scheduled_end,
                status: apt.status,
                item_number: apt.item_number
              }));
          } else {
            newAvailability[userId] = [];
          }
        } catch {
          newAvailability[userId] = [];
        }
      }));

      if (options.updateState !== false) {
        setAvailability(newAvailability);
      }

      return newAvailability;
    } catch (error) {
      console.error('Error fetching availability:', error);
      return {};
    } finally {
      setLoadingAvailability(false);
    }
  }, [getAuthToken]);

  const resetForm = () => {
    setFormData(buildInitialFormData());
    setExpandedRequirements(false);
    setRecurrenceRule({
      enabled: false,
      frequency: 'weekly',
      intervalCount: 1,
      occurrenceCount: 4,
    });
    setSelectedContact(null);
    setSelectedLocation(null);
    setSelectedPerson(null);
    setSelectedJob(null);
    setJobSearchQuery('');
    setFilteredJobs([]);
    setAvailableJobs([]);
    setLocations([]);
    setContactPeople([]);
    setLoadingLocations(false);
    setLoadingPeople(false);
    setLocationLoadError(null);
    setShowJobCreateForm(false);
    setShowLocationCreate(false);
    setNewJobDescription('');
    setLocationSource('contact');
    setCustomLocation(null);
    setCustomLocSearch('');
    setJobsChecked(false);
    setSearchingJobs(false);
    setSelectedPurchaseOrders([]);
    setPoSearchQuery('');
    setPoSearchResults([]);
    setIsSearchingPO(false);
    setShowPOSearch(false);
    latestLocationLoadRequestId.current += 1;
    latestPeopleLoadRequestId.current += 1;
    latestJobLoadRequestId.current += 1;
    activeContactIdRef.current = null;
    appliedPresetJobIdRef.current = null;
  };

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Load initial data when modal opens
  useEffect(() => {
    if (isOpen) {
      emitOperationsGuidedTourTrainingEvent({
        type: 'appointment_modal_opened',
        route: 'appointment_modal',
      });
      void fetchContacts();
    }
  }, [fetchContacts, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      appliedPresetJobIdRef.current = null;
      return;
    }

    if (!presetJob?.id) {
      return;
    }

    const presetJobId = `preset:${String(presetJob.id)}`;
    if (appliedPresetJobIdRef.current === presetJobId) {
      return;
    }

    const normalizedPresetJob: AppointmentModalPresetJob = {
      ...presetJob,
      contact_id: normalizePositiveIntegerId(presetJob.contact_id),
      contact_person_id: normalizePositiveIntegerId(presetJob.contact_person_id),
      contact_person_id2: normalizePositiveIntegerId(presetJob.contact_person_id2),
      contact_person_id3: normalizePositiveIntegerId(presetJob.contact_person_id3),
      location_id: normalizePositiveIntegerId(presetJob.location_id),
      custom_fields: parseJobCustomFields(presetJob.custom_fields),
    };

    setSelectedJob(normalizedPresetJob);
    setJobSearchQuery('');
    setShowJobCreateForm(false);
    setNewJobDescription('');
    setFormData((prev) => ({
      ...prev,
      title: prev.title || normalizedPresetJob.title || `Appointment for ${normalizedPresetJob.item_number}`,
      description: prev.description || normalizedPresetJob.description || '',
      additional_notes: prev.additional_notes || normalizedPresetJob.notes || '',
    }));

    if (normalizedPresetJob.contact_id) {
      const existingContact = contacts.find((contact) => Number(contact.id) === Number(normalizedPresetJob.contact_id));
      const fallbackContact: Contact = existingContact || {
        id: normalizedPresetJob.contact_id,
        company_name: normalizedPresetJob.contact_name || null,
        contact_person: normalizedPresetJob.contact_person || null,
        email: null,
        phone: null,
        contact_type: 'customer',
      };

      activeContactIdRef.current = fallbackContact.id;
      setSelectedContact((prev) => Number(prev?.id) === Number(fallbackContact.id) ? prev : fallbackContact);
    }

    const inheritedLocation = buildSyntheticLocationFromJob(normalizedPresetJob);
    if (inheritedLocation) {
      setLocationSource('contact');
      setSelectedLocation((prev) => Number(prev?.id) === Number(inheritedLocation.id) ? prev : inheritedLocation);
      setCustomLocation(null);
      setCustomLocSearch('');
    } else {
      const inheritedCustomLocation = buildCustomLocationFromJob(normalizedPresetJob);
      if (inheritedCustomLocation) {
        setLocationSource('custom');
        setSelectedLocation(null);
        setCustomLocation(inheritedCustomLocation);
        setCustomLocSearch(inheritedCustomLocation.name);
      }
    }

    appliedPresetJobIdRef.current = presetJobId;
  }, [contacts, isOpen, presetJob]);

  // Load locations when contact is selected
  useEffect(() => {
    setShowJobCreateForm(false);
    setShowLocationCreate(false);
    setNewJobDescription('');

    if (selectedContactId) {
      void fetchLocationsForContact(selectedContactId);
      void fetchContactPeople(selectedContactId);
      void fetchActiveJobs(selectedContactId);
    } else {
      activeContactIdRef.current = null;
      latestLocationLoadRequestId.current += 1;
      latestPeopleLoadRequestId.current += 1;
      latestJobLoadRequestId.current += 1;
      setLocations([]);
      setContactPeople([]);
      setAvailableJobs([]);
      setFilteredJobs([]);
      setSelectedLocation(null);
      setSelectedPerson(null);
      setSelectedJob(null);
      setLoadingLocations(false);
      setLoadingPeople(false);
      setSearchingJobs(false);
      setLocationLoadError(null);
    }

    // Reset purchase order selection when contact changes or is no longer a supplier
    setSelectedPurchaseOrders([]);
    setPoSearchQuery('');
    setPoSearchResults([]);
    setShowPOSearch(false);
  }, [selectedContactId, fetchLocationsForContact, fetchContactPeople, fetchActiveJobs]);

  useEffect(() => {
    if (!isOpen || !presetJob?.id || !selectedJob || String(selectedJob.id) !== String(presetJob.id)) {
      return;
    }

    // Only auto-set from preset job if user hasn't manually chosen a different location source
    if (locationSource !== 'custom' && !selectedLocation && presetJob.location_id && locations.length > 0) {
      const presetLocation = locations.find((location) => Number(location.id) === Number(presetJob.location_id));
      if (presetLocation) {
        setLocationSource('contact');
        setSelectedLocation(presetLocation);
      }
    }

    if (!selectedPerson && presetJob.contact_person_id && contactPeople.length > 0) {
      const presetPerson = contactPeople.find((person) => Number(person.id) === Number(presetJob.contact_person_id));
      if (presetPerson) {
        setSelectedPerson(presetPerson);
      }
    }

    if (locationSource !== 'custom' && !presetJob.location_id && presetJob.location_name && !selectedLocation && !customLocation) {
      let lat = '';
      let lng = '';

      if (presetJob.location_coordinates) {
        const coordStr = String(presetJob.location_coordinates).replace(/[()]/g, '');
        const parts = coordStr.split(',').map((part) => part.trim());
        if (parts.length === 2) {
          lat = parts[0] || '';
          lng = parts[1] || '';
        }
      }

      setLocationSource('custom');
      setCustomLocation({
        name: presetJob.location_name,
        address: presetJob.location_address || '',
        lat,
        lng,
      });
      setCustomLocSearch(presetJob.location_name);
    }
  }, [contactPeople, customLocation, isOpen, locations, presetJob, selectedJob, selectedLocation, selectedPerson, locationSource]);

  // Pre-populate from parentJob when fromJobContext is true
  useEffect(() => {
    if (!isOpen || !fromJobContext || !parentJob) {
      return;
    }

    const parentJobId = `parent:${String(parentJob.id)}`;
    if (appliedPresetJobIdRef.current === parentJobId) {
      return;
    }

    const normalizedParentJob: AppointmentModalPresetJob = {
      id: parentJob.id,
      item_number: parentJob.item_number,
      title: parentJob.title,
      description: parentJob.description || '',
      notes: parentJob.notes || '',
      status: parentJob.status || 'open',
      item_type: parentJob.item_type || null,
      item_subtype: parentJob.item_subtype || null,
      module_context: parentJob.module_context || null,
      contact_id: normalizePositiveIntegerId(parentJob.contact_id),
      contact_name: parentJob.contact_name || null,
      contact_person: parentJob.contact_person || null,
      contact_person_id: normalizePositiveIntegerId(parentJob.contact_person_id),
      contact_person_id2: normalizePositiveIntegerId(parentJob.contact_person_id2),
      contact_person_id3: normalizePositiveIntegerId(parentJob.contact_person_id3),
      location_id: normalizePositiveIntegerId(parentJob.location_id),
      location_name: parentJob.location_name || null,
      location_address: parentJob.location_address || null,
      location_coordinates: parentJob.location_coordinates || null,
      custom_fields: parseJobCustomFields(parentJob.custom_fields),
    };

    setSelectedJob(normalizedParentJob);
    setJobSearchQuery('');
    setShowJobCreateForm(false);
    setNewJobDescription('');
    setFormData((prev) => ({
      ...prev,
      title: prev.title || normalizedParentJob.title || `Appointment for ${normalizedParentJob.item_number}`,
      description: prev.description || normalizedParentJob.description || '',
      additional_notes: prev.additional_notes || normalizedParentJob.notes || '',
      equipment_required: normalizedParentJob.custom_fields?.equipment_required || '',
      safety_requirements: normalizedParentJob.custom_fields?.safety_requirements || '',
    }));

    if (normalizedParentJob.contact_id) {
      const existingContact = contacts.find((contact) => Number(contact.id) === Number(normalizedParentJob.contact_id));
      const fallbackContact: Contact = existingContact || {
        id: normalizedParentJob.contact_id,
        company_name: normalizedParentJob.contact_name || null,
        contact_person: normalizedParentJob.contact_person || null,
        email: null,
        phone: null,
        contact_type: 'customer',
      };

      activeContactIdRef.current = fallbackContact.id;
      setSelectedContact((prev) => Number(prev?.id) === Number(fallbackContact.id) ? prev : fallbackContact);
    }

    const inheritedLocation = buildSyntheticLocationFromJob(normalizedParentJob);
    if (inheritedLocation) {
      setLocationSource('contact');
      setSelectedLocation((prev) => Number(prev?.id) === Number(inheritedLocation.id) ? prev : inheritedLocation);
      setCustomLocation(null);
      setCustomLocSearch('');
    } else {
      const inheritedCustomLocation = buildCustomLocationFromJob(normalizedParentJob);
      if (inheritedCustomLocation) {
        setLocationSource('custom');
        setSelectedLocation(null);
        setCustomLocation(inheritedCustomLocation);
        setCustomLocSearch(inheritedCustomLocation.name);
      }
    }

    appliedPresetJobIdRef.current = parentJobId;
  }, [fromJobContext, parentJob, isOpen, contacts]);

  useEffect(() => {
    if (!isOpen || !fromJobContext || !parentJob || !selectedJob || String(selectedJob.id) !== String(parentJob.id)) {
      return;
    }

    // Only auto-set from parent job if user hasn't manually chosen a different location source
    if (locationSource !== 'custom' && !selectedLocation && parentJob.location_id && locations.length > 0) {
      const parentLocation = locations.find((location) => Number(location.id) === Number(parentJob.location_id));
      if (parentLocation) {
        setLocationSource('contact');
        setSelectedLocation(parentLocation);
      }
    }

    if (!selectedPerson && parentJob.contact_person_id && contactPeople.length > 0) {
      const parentPerson = contactPeople.find((person) => Number(person.id) === Number(parentJob.contact_person_id));
      if (parentPerson) {
        setSelectedPerson(parentPerson);
      }
    }

    if (locationSource !== 'custom' && !parentJob.location_id && parentJob.location_name && !selectedLocation && !customLocation) {
      const inheritedCustomLocation = buildCustomLocationFromJob({
        id: parentJob.id,
        item_number: parentJob.item_number,
        title: parentJob.title,
        description: parentJob.description || '',
        notes: parentJob.notes || '',
        status: 'open',
        module_context: null,
        contact_id: normalizePositiveIntegerId(parentJob.contact_id),
        contact_name: parentJob.contact_name || null,
        contact_person: parentJob.contact_person || null,
        contact_person_id: normalizePositiveIntegerId(parentJob.contact_person_id),
        location_id: normalizePositiveIntegerId(parentJob.location_id),
        location_name: parentJob.location_name || null,
        location_address: parentJob.location_address || null,
        location_coordinates: parentJob.location_coordinates || null,
        custom_fields: parseJobCustomFields(parentJob.custom_fields),
      });

      if (inheritedCustomLocation) {
        setLocationSource('custom');
        setCustomLocation(inheritedCustomLocation);
        setCustomLocSearch(inheritedCustomLocation.name);
      }
    }
  }, [contactPeople, customLocation, fromJobContext, isOpen, locations, parentJob, selectedJob, selectedLocation, selectedPerson, locationSource]);

  // Filter jobs by search query
  useEffect(() => {
    if (!jobSearchQuery.trim()) {
      setFilteredJobs(availableJobs);
    } else {
      const query = jobSearchQuery.toLowerCase();
      setFilteredJobs(
        availableJobs.filter(job =>
          job.item_number.toLowerCase().includes(query) ||
          job.title.toLowerCase().includes(query) ||
          (job.contact_name?.toLowerCase().includes(query) || false) ||
          (job.location_name?.toLowerCase().includes(query) || false)
        )
      );
    }
  }, [jobSearchQuery, availableJobs]);

  // Fetch availability when team members or scheduled date changes
  useEffect(() => {
    if (selectedAssignmentUserIds.length > 0 && selectedDateStr) {
      void fetchAvailability(selectedAssignmentUserIds, selectedDateStr);
    } else {
      setAvailability({});
    }
  }, [selectedAssignmentUserIds, selectedDateStr, fetchAvailability]);

  useEffect(() => {
    if (!selectedJob || !isPurchaseAppointmentType || purchaseItems.length > 0) {
      return;
    }

    const inheritedPurchaseItems = normalizePurchaseListItems(selectedJob.custom_fields?.purchase_items);
    if (inheritedPurchaseItems.length === 0) {
      return;
    }

    setFormData(prev => ({
      ...prev,
      purchase_items: inheritedPurchaseItems,
    }));
  }, [isPurchaseAppointmentType, purchaseItems.length, selectedJob]);


  // ============================================================================
  // HANDLERS
  // ============================================================================

  // Extra contact people can be copied from the parent job only while the
  // appointment keeps the job's primary contact person.
  const jobHasExtraContactPeople = selectedJob != null
    && (selectedJob.contact_person_id2 != null || selectedJob.contact_person_id3 != null);
  const showInheritPeopleToggle = locationSource === 'contact'
    && jobHasExtraContactPeople
    && selectedPerson?.id != null
    && selectedJob?.contact_person_id != null
    && Number(selectedPerson.id) === Number(selectedJob.contact_person_id);

  const handleContactSelect = (contact: Contact) => {
    activeContactIdRef.current = contact.id;
    setSelectedContact(contact);
    setSelectedLocation(null);
    setSelectedPerson(null);
    setSelectedJob(null);
    setLocationLoadError(null);
    setLocationSource('contact');
    setCustomLocation(null);
    setCustomLocSearch('');
    setJobsChecked(false);
  };

  const handleSharedContactChange = useCallback((contactId: number | null, contactName?: string) => {
    if (contactId == null) {
      activeContactIdRef.current = null;
      setSelectedContact(null);
      setSelectedLocation(null);
      setSelectedPerson(null);
      setSelectedJob(null);
      setLocationLoadError(null);
      setLocationSource('contact');
      setCustomLocation(null);
      setCustomLocSearch('');
      setJobsChecked(false);
      return;
    }

    const existingContact = contacts.find((contact) => Number(contact.id) === Number(contactId));
    handleContactSelect(existingContact || {
      id: contactId,
      company_name: contactName || null,
      contact_person: null,
      email: null,
      phone: null,
      contact_type: 'customer',
    });
  }, [contacts]);

  const handleSharedLocationChange = useCallback((locationId: number | null) => {
    if (locationId == null) {
      setSelectedLocation(null);
      return;
    }

    const location = locations.find((entry) => Number(entry.id) === Number(locationId));
    setSelectedLocation(location || null);
  }, [locations]);

  const handleSharedContactPersonChange = useCallback((personId: number | null) => {
    if (personId == null) {
      setSelectedPerson(null);
      return;
    }

    const person = contactPeople.find((entry) => Number(entry.id) === Number(personId));
    setSelectedPerson(person || null);
  }, [contactPeople]);

  const handleSharedLocationCreated = useCallback((newLocation: UnifiedLookupLocation) => {
    const mappedLocation: Location = {
      id: newLocation.id,
      contact_id: newLocation.contact_id,
      location_name: newLocation.location_name,
      address_line1: newLocation.address_line1,
      city: newLocation.city,
      is_primary: newLocation.is_primary,
      latitude: null,
      longitude: null,
    };

    const cachedLocations = contactLocationsCacheRef.current.get(mappedLocation.contact_id) || [];
    contactLocationsCacheRef.current.set(
      mappedLocation.contact_id,
      [...cachedLocations.filter((location) => Number(location.id) !== Number(mappedLocation.id)), mappedLocation]
    );

    setLocations((prev) => {
      const next = prev.filter((location) => Number(location.id) !== Number(mappedLocation.id));
      return [...next, mappedLocation];
    });
    setSelectedLocation(mappedLocation);
  }, []);

  const handleSharedPersonCreated = useCallback((newPerson: UnifiedLookupContactPerson) => {
    const mappedPerson: ContactPerson = {
      id: newPerson.id,
      contact_id: newPerson.contact_id,
      first_name: newPerson.first_name,
      last_name: newPerson.last_name,
      job_title: newPerson.job_title,
      email: newPerson.email,
      phone: newPerson.phone,
    };

    setContactPeople((prev) => {
      const next = prev.filter((person) => Number(person.id) !== Number(mappedPerson.id));
      return [...next, mappedPerson];
    });
    setSelectedPerson(mappedPerson);
  }, []);

  const handleUnifiedSelect = (result: UnifiedSearchResult) => {
    activeContactIdRef.current = result.contact_id;
    // Auto-select the contact from the unified search result
    const contact = contacts.find(c => c.id === result.contact_id);
    if (contact) {
      setSelectedContact(contact);
    } else {
      // Create a temporary contact object if not in our loaded list
      setSelectedContact({
        id: result.contact_id,
        company_name: result.contact_company_name || result.contact_name,
        contact_person: null,
        email: null,
        phone: null,
        contact_type: 'customer'
      });
    }

    // If it was a location search, pre-select the location
    if (result.type === 'location' && result.location_id) {
      setSelectedLocation({
        id: result.location_id,
        contact_id: result.contact_id,
        location_name: result.location_name || '',
        address_line1: result.location_address,
        city: result.location_city,
        is_primary: false,
        latitude: result.location_latitude ?? null,
        longitude: result.location_longitude ?? null,
      });
    }

    // If it was a person search, pre-select the person
    if (result.type === 'person' && result.person_id) {
      setSelectedPerson({
        id: result.person_id,
        contact_id: result.contact_id,
        first_name: (result.person_name || '').split(' ')[0] || '',
        last_name: (result.person_name || '').split(' ').slice(1).join(' ') || '',
        job_title: null,
        email: result.person_email || null,
        phone: null
      });
    }
  };

  useEffect(() => {
    if (loadingPeople) {
      return;
    }

    if (selectedPerson && !contactPeople.some((person) => Number(person.id) === Number(selectedPerson.id))) {
      setSelectedPerson(null);
    }
  }, [contactPeople, loadingPeople, selectedPerson]);

  const handleJobSelect = (job: JobSearchResult) => {
    setSelectedJob(job);
    setJobSearchQuery('');
    setIsJobDropdownOpen(false);
    setShowJobCreateForm(false);
    setNewJobDescription('');
    // Set title based on job if empty
    if (!formData.title) {
      setFormData(prev => ({ ...prev, title: job.title || `Appointment for ${job.item_number}` }));
    }

    // --- Location inheritance from parent job (only if no location is already set) ---
    // Priority 1: Job has a contact_location (location_id) → contact mode (only if location has coordinates)
    // Respect user's explicit source choice (custom) even if no location entered yet
    const userExplicitlyChoseSource = locationSource === 'custom';
    const alreadyHasLocation = locationSource === 'custom' ? !!customLocation : !!selectedLocation;
    if (!userExplicitlyChoseSource && !alreadyHasLocation && job.location_id) {
      const jobLoc = locations.find(l => Number(l.id) === Number(job.location_id));
      if (jobLoc && hasContactLocationCoordinates(jobLoc)) {
        setLocationSource('contact');
        setSelectedLocation(jobLoc);
        setCustomLocation(null);
        setCustomLocSearch('');
      }
    }
    // Priority 2: Job has a custom location (no location_id, but has location_name) → custom mode
    else if (!userExplicitlyChoseSource && !alreadyHasLocation && !job.location_id && job.location_name) {
      // Parse coordinates if available (PostgreSQL point format: "(lng,lat)" or "(lat,lng)")
      let lat = '';
      let lng = '';
      if (job.location_coordinates) {
        const coordStr = String(job.location_coordinates).replace(/[()]/g, '');
        const parts = coordStr.split(',').map((s: string) => s.trim());
        if (parts.length === 2) {
          lat = parts[0];
          lng = parts[1];
        }
      }
      setLocationSource('custom');
      setCustomLocation({
        name: job.location_name,
        address: job.location_address || '',
        lat,
        lng,
      });
      setCustomLocSearch(job.location_name);
      setSelectedLocation(null);
    }
    // --- Person inheritance from parent job ---
    if (!selectedPerson && job.contact_person_id) {
      const jobPerson = contactPeople.find(p => Number(p.id) === Number(job.contact_person_id));
      if (jobPerson) {
        setSelectedPerson(jobPerson);
      }
    }

    // Inherit require_location_verification from parent job's custom_fields
    const jobCustomFields = job.custom_fields || {};
    const requireVerification = jobCustomFields.require_exact_location || jobCustomFields.require_location_verification || false;
    const geofenceRadius = jobCustomFields.geofence_radius_meters || 100;
    setFormData(prev => {
      const isPurchaseType = normalizeAppointmentTypeKey(prev.item_subtype) === PURCHASE_APPOINTMENT_TYPE_KEY;
      const inheritedPurchaseItems = normalizePurchaseListItems(jobCustomFields.purchase_items);

      return {
        ...prev,
        require_location_verification: requireVerification,
        geofence_radius_meters: geofenceRadius,
        // Inherit description from job (if not already typed)
        description: prev.description || job.description || '',
        additional_notes: job.notes || jobCustomFields.additional_notes || '',
        // Inherit equipment & safety from job
        equipment_required: isPurchaseType ? prev.equipment_required : jobCustomFields.equipment_required || '',
        safety_requirements: isPurchaseType ? prev.safety_requirements : jobCustomFields.safety_requirements || '',
        purchase_items: isPurchaseType && prev.purchase_items.length === 0 ? inheritedPurchaseItems : prev.purchase_items,
      };
    });
  };

  const handleCreateJob = async () => {
    if (!selectedContact) {
      notify.error('Please select a contact before creating a job');
      return;
    }

    const trimmedDescription = newJobDescription.trim();
    if (!trimmedDescription) {
      notify.error('Please enter a job description');
      return;
    }

    const contactName = selectedContact.company_name || selectedContact.contact_person || selectedContact.email || null;
    const jobTitle = deriveInlineJobTitle(trimmedDescription, contactName);
    const contactLocationCoordinates =
      locationSource === 'contact' && selectedLocation
        ? getContactLocationCoordinates(selectedLocation)
        : null;
    const customLocationCoordinates =
      locationSource === 'custom'
        ? extractCoordinates({ lat: customLocation?.lat, lng: customLocation?.lng })
        : null;

    const payload = {
      item_type: 'job',
      module_context: 'service-operations',
      title: jobTitle,
      description: trimmedDescription,
      status: 'open',
      priority: 'medium',
      contact_id: selectedContact.id,
      contact_name: contactName,
      contact_email: selectedContact.email || undefined,
      contact_phone: selectedContact.phone || undefined,
      ...(locationSource === 'contact' && selectedLocation
        ? {
          location_id: selectedLocation.id,
        }
        : {}),
      ...(locationSource === 'custom' && customLocation
        ? {
          location_name: customLocation.name || null,
          location_address: customLocation.address || null,
          ...(customLocationCoordinates ? { location_coordinates: customLocationCoordinates } : {}),
        }
        : {}),
      ...(locationSource === 'contact' && selectedLocation && contactLocationCoordinates
        ? {
          location_coordinates: contactLocationCoordinates,
        }
        : {}),
      ...(locationSource === 'contact' && selectedPerson
        ? {
          contact_person_id: selectedPerson.id,
          contact_person: `${selectedPerson.first_name} ${selectedPerson.last_name}`.trim() || undefined,
        }
        : {}),
    };

    setCreatingJob(true);
    try {
      const response = await fetch('/api/v1/work-items', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      if (!response.ok || !result.success || !result.data) {
        throw new Error(result.error || 'Failed to create job');
      }

      const createdJobData = result.data;
      const normalizedJob: JobSearchResult = {
        id: String(createdJobData.id),
        item_number: createdJobData.item_number,
        title: createdJobData.title,
        description: createdJobData.description ?? null,
        notes: createdJobData.notes ?? null,
        status: createdJobData.status,
        module_context: typeof createdJobData.module_context === 'string' ? createdJobData.module_context : 'service-operations',
        contact_id: createdJobData.contact_id ?? null,
        contact_name: createdJobData.contact_name ?? null,
        contact_person: createdJobData.contact_person ?? null,
        contact_person_id: createdJobData.contact_person_id ?? null,
        location_id: createdJobData.location_id ?? null,
        location_name: createdJobData.location_name ?? null,
        location_address: createdJobData.location_address ?? null,
        location_coordinates: createdJobData.location_coordinates ?? null,
        custom_fields: parseJobCustomFields(createdJobData.custom_fields),
      };

      if (selectedContactId) {
        const cachedJobs = contactJobsCacheRef.current.get(selectedContactId) || [];
        contactJobsCacheRef.current.set(
          selectedContactId,
          [normalizedJob, ...cachedJobs.filter((job) => String(job.id) !== normalizedJob.id)]
        );
      }

      setAvailableJobs(prev => [normalizedJob, ...prev.filter(job => String(job.id) !== normalizedJob.id)]);
      setFilteredJobs(prev => [normalizedJob, ...prev.filter(job => String(job.id) !== normalizedJob.id)]);
      setJobsChecked(true);
      handleJobSelect(normalizedJob);
      setJobSearchQuery('');

      emit('workItemCreated', { item: createdJobData, itemType: 'job' });

      notify.success(`Created job ${createdJobData.item_number}`);
    } catch (error: unknown) {
      console.error('Error creating inline job:', error);
      notify.error(getErrorMessage(error) || 'Failed to create job');
    } finally {
      setCreatingJob(false);
    }
  };

  const handleSubmit = async () => {
    // Immediately set loading state to prevent double-clicks
    setCreating(true);

    // AbortController for cancelling requests on error/unmount
    const abortController = new AbortController();
    const timeoutId = setTimeout(() => abortController.abort(), 30000); // 30 second timeout

    const finalizedPurchaseItems = isPurchaseAppointmentType
      ? purchaseItemListRef.current?.commitPendingCapture() ?? purchaseItems
      : purchaseItems;

    // Validation
    if (!formData.title) {
      notify.error('Title is required');
      setCreating(false);
      return;
    }

    if (!formData.item_subtype) {
      notify.error('Appointment type is required');
      setCreating(false);
      return;
    }

    if (!formData.priority) {
      notify.error('Priority is required');
      setCreating(false);
      return;
    }

    if (!selectedJob) {
      notify.error('Please select a job to link this appointment to');
      setCreating(false);
      return;
    }

    // Account-standing / credit-limit pre-flight. Honours tenant
    // credit_control settings (warn or hard-stop).
    {
      const contactIdForWarning = selectedContact?.id ?? null;
      if (contactIdForWarning) {
        const { warnIfContactAccountIssue } = await import('@/lib/contacts/accountWarning');
        const proceed = await warnIfContactAccountIssue({
          contactId: contactIdForWarning,
          action: 'schedule this appointment',
          documentType: 'appointment',
        });
        if (!proceed) {
          setCreating(false);
          return;
        }
      }
    }

    if (locationSource === 'contact' && !selectedLocation) {
      if (selectedJob?.location_id) {
        const jobLoc = locations.find(l => Number(l.id) === Number(selectedJob.location_id));
        if (jobLoc && !hasContactLocationCoordinates(jobLoc)) {
          notify.error(`The job's location "${jobLoc.location_name || 'selected location'}" does not have GPS coordinates. Please select a different location with coordinates in the appointment location section.`);
          setCreating(false);
          return;
        }
      }
      // No contact location selected. This commonly happens when a contact was
      // created in total-finance (or imported via QBO/Sage/Xero) without GPS
      // coordinates. Offer a blocking prompt to capture a location inline.
      setCreating(false);
      const contactLabel = selectedContact?.company_name || selectedContact?.contact_person || 'this contact';
      const proceed = await confirm({
        title: 'GPS coordinates required',
        message: `${contactLabel} has no appointment location with GPS coordinates. Appointments require exact coordinates for geofence check-in. Add a location with coordinates now?`,
        confirmText: 'Add Location',
        cancelText: 'Cancel',
        variant: 'warning',
      });
      if (proceed) {
        setShowLocationCreate(true);
      }
      return;
    }
    if (locationSource === 'custom' && !customLocation) {
      notify.error('Please search and select a custom location using Google Maps');
      setCreating(false);
      return;
    }

    const resolvedCoordinates = (() => {
      if (locationSource === 'custom') {
        return extractCoordinates({ lat: customLocation?.lat, lng: customLocation?.lng });
      }

      return getContactLocationCoordinates(selectedLocation);
    })();

    const coordinateLabel = locationSource === 'custom'
      ? customLocation?.name || 'custom appointment location'
      : selectedLocation?.location_name || 'selected appointment location';

    if (!resolvedCoordinates) {
      // The selected contact location exists but has no GPS coordinates (e.g. a
      // finance-created contact). Offer to capture a coordinate-bearing location.
      if (locationSource === 'contact' && selectedContact) {
        setCreating(false);
        const contactLabel = selectedContact.company_name || selectedContact.contact_person || 'this contact';
        const proceed = await confirm({
          title: 'GPS coordinates required',
          message: `The selected location for ${contactLabel} has no GPS coordinates. Appointments require exact coordinates for geofence check-in. Add a location with coordinates now?`,
          confirmText: 'Add Location',
          cancelText: 'Cancel',
          variant: 'warning',
        });
        if (proceed) {
          setShowLocationCreate(true);
        }
        return;
      }
      notify.error(buildMissingCoordinatesMessage(coordinateLabel));
      setCreating(false);
      return;
    }

    if (!formData.assigned_team && formData.assigned_to.length === 0) {
      notify.error('Please assign an operations team or at least one team member');
      setCreating(false);
      return;
    }

    if (!formData.scheduled_start) {
      notify.error('Scheduled start time is required');
      setCreating(false);
      return;
    }

    if (!formData.scheduled_end) {
      notify.error('Scheduled end time is required');
      setCreating(false);
      return;
    }

    if (!scheduleValidation.isValid) {
      notify.error(scheduleError || 'Please correct the appointment schedule before continuing');
      setCreating(false);
      return;
    }

    const proposedStart = new Date(formData.scheduled_start);
    const proposedEnd = new Date(formData.scheduled_end);
    const latestAvailability = selectedDateStr
      ? await fetchAvailability(selectedAssignmentUserIds, selectedDateStr)
      : availability;

    for (const userId of selectedAssignmentUserIds) {
      const clashingItems = findAppointmentClashes(latestAvailability[userId] || [], {
        newStart: proposedStart,
        newEnd: proposedEnd,
        excludeHiddenStatuses: true,
      });

      if (clashingItems.length === 0) continue;

      const tech = effectiveTechnicians.find(member => member.id === userId);
      const clashDetails = clashingItems.map(item =>
        `${item.item_number || item.title} (${formatTimeSAST(item.scheduled_start)} - ${formatTimeSAST(item.scheduled_end)})`
      ).join('\n');

      setCreating(false);
      const shouldContinue = await confirm({
        title: 'Appointment Clash Detected',
        message: `Creating this appointment for ${tech?.name || 'this team member'} will overlap with existing appointments:\n\n${clashDetails}\n\nDo you want to create it anyway?`,
        confirmText: 'Create Anyway',
        cancelText: 'Cancel',
        variant: 'warning',
        onConfirm: () => { },
      });

      if (!shouldContinue) {
        return;
      }
      setCreating(true);
    }
    try {
      const assignees = selectedAssignmentUserIds;
      const assigneeUserIds = assignees.filter((id) => id > 0);
      const assigneeContractorIds = assignees.filter((id) => id < 0).map((id) => Math.abs(id));
      let successCount = 0;
      let failCount = 0;
      const createdAppointmentIds: string[] = [];
      const formatWithTZ = (localStr: string): string => formatLocalISOSAST(new Date(localStr));
      const recurrenceSeriesId = recurrenceRule.enabled && recurringOccurrences.length > 1
        ? `${selectedJob.id}:${formData.scheduled_start}:${recurrenceRule.frequency}:${recurrenceRule.intervalCount}`
        : null;
      const primaryAssigneeId = selectedTeamLeaderUserId || assignees[0] || null;
      const isJobParent = (selectedJob.item_type ?? 'job') === 'job';
      const resolvedAppointmentModuleContext = moduleContext?.trim()
        || (typeof selectedJob.module_context === 'string' && selectedJob.module_context.trim()
          ? selectedJob.module_context.trim()
          : 'service-operations');

      // Extra contact people are inherited only when the appointment keeps the
      // job's primary person — picking a different person means the extras
      // (who belong to that person context) shouldn't be copied over.
      const inheritExtraContactPeople = locationSource === 'contact'
        && formData.inherit_all_contact_people
        && selectedPerson?.id != null
        && selectedJob.contact_person_id != null
        && Number(selectedPerson.id) === Number(selectedJob.contact_person_id);

      if (!primaryAssigneeId) {
        throw new Error('Please select at least one valid assignee for the appointment');
      }

      for (const occurrence of recurringOccurrences) {
        try {
          const appointmentPayload = {
            item_type: 'appointment',
            item_subtype: formData.item_subtype,
            parent_id: selectedJob.id,
            title: formData.title,
            description: formData.description,
            notes: formData.additional_notes || undefined,
            status: formData.status,
            priority: formData.priority,
            contact_id: selectedContact?.id || null,
            contact_name: selectedContact?.company_name || selectedContact?.contact_person || null,
            contact_person_id: (() => {
              if (locationSource === 'custom') return null;
              return selectedPerson?.id || null;
            })(),
            contact_person_id2: inheritExtraContactPeople ? (selectedJob.contact_person_id2 ?? null) : null,
            contact_person_id3: inheritExtraContactPeople ? (selectedJob.contact_person_id3 ?? null) : null,
            contact_person: (() => {
              if (locationSource === 'custom' && formData.custom_contact_person) return formData.custom_contact_person;
              return selectedPerson ? `${selectedPerson.first_name} ${selectedPerson.last_name}`.trim() : null;
            })(),
            location_id: (() => {
              if (locationSource === 'custom') return null;
              return selectedLocation?.id || null;
            })(),
            location_name: locationSource === 'custom' ? customLocation?.name || null : selectedLocation?.location_name || null,
            location_address: (() => {
              if (locationSource === 'custom') return customLocation?.address || null;
              if (selectedLocation) return `${selectedLocation.location_name}${selectedLocation.address_line1 ? ', ' + selectedLocation.address_line1 : ''}${selectedLocation.city ? ', ' + selectedLocation.city : ''}`;
              return null;
            })(),
            location_coordinates: resolvedCoordinates,
            scheduled_start: occurrence.scheduled_start ? formatWithTZ(occurrence.scheduled_start) : null,
            scheduled_end: occurrence.scheduled_end ? formatWithTZ(occurrence.scheduled_end) : null,
            assigned_to: primaryAssigneeId,
            assigned_team: selectedAssignmentTeam?.id || null,
            assigned_users: assigneeUserIds,
            assigned_contractors: assigneeContractorIds.length > 0 ? assigneeContractorIds : undefined,
            module_context: resolvedAppointmentModuleContext,
            custom_fields: {
              require_location_verification: formData.require_location_verification,
              geofence_radius_meters: formData.geofence_radius_meters,
              parent_job_id: isJobParent ? selectedJob.id : undefined,
              parent_job_number: isJobParent ? selectedJob.item_number : undefined,
              parent_item_id: selectedJob.id,
              parent_item_number: selectedJob.item_number,
              parent_item_type: selectedJob.item_type || 'job',
              parent_item_subtype: selectedJob.item_subtype || undefined,
              equipment_required: isPurchaseAppointmentType ? undefined : formData.equipment_required || undefined,
              safety_requirements: isPurchaseAppointmentType ? undefined : formData.safety_requirements || undefined,
              purchase_items: isPurchaseAppointmentType ? finalizedPurchaseItems : undefined,
              recurrence_series_id: recurrenceSeriesId,
              recurrence_rule: recurrenceRule.enabled ? recurrenceRule : undefined,
              recurrence_sequence: recurrenceRule.enabled ? occurrence.sequence : undefined,
            }
          };

          const createRes = await fetch('/api/v1/work-items', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${getAuthToken()}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(appointmentPayload),
            signal: abortController.signal
          });

          const createData = await createRes.json();
          if (!createData.success) {
            const err: Error & { code?: string; details?: { contact_name?: string } } = new Error(createData.error || 'Failed to create appointment');
            if (createData.code) err.code = createData.code;
            if (createData.details) err.details = createData.details;
            throw err;
          }

          const appointmentId = createData.item?.id || createData.data?.id;
          if (appointmentId) {
            createdAppointmentIds.push(appointmentId);
            try {
              await fetch('/api/v1/work-item-relationships', {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${getAuthToken()}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  action: 'create',
                  sourceItemId: appointmentId,
                  targetItemId: selectedJob.id,
                  relationshipType: 'child_of'
                }),
                signal: abortController.signal
              });
            } catch (relError) {
              console.warn('[AppointmentModal] Failed to create work-item relationship:', relError);
            }

            // Link appointment to selected purchase orders
            if (isExpenseRequiredAppointmentType && selectedPurchaseOrders.length > 0) {
              for (const po of selectedPurchaseOrders) {
                try {
                  await fetch('/api/v1/work-item-relationships', {
                    method: 'POST',
                    headers: {
                      'Authorization': `Bearer ${getAuthToken()}`,
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                      action: 'create',
                      sourceItemId: appointmentId,
                      targetItemId: po.id,
                      relationshipType: 'relates_to'
                    }),
                    signal: abortController.signal
                  });
                } catch (poRelError) {
                  console.warn('[AppointmentModal] Failed to link appointment to purchase order:', poRelError);
                }
              }
            }
          }

          if (appointmentId) {
            try {
              const notifyRes = await fetch('/api/v1/messaging/job-threads', {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${getAuthToken()}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  action: 'notifyAppointmentScheduled',
                  job_id: selectedJob.id,
                  appointment_id: appointmentId,
                  assignee_user_ids: assigneeUserIds,
                  scheduled_start: occurrence.scheduled_start ? formatWithTZ(occurrence.scheduled_start) : null,
                  scheduled_end: occurrence.scheduled_end ? formatWithTZ(occurrence.scheduled_end) : null,
                  appointment_title: formData.title
                }),
                signal: abortController.signal
              });
              const notifyData = await notifyRes.json();
              if (!notifyData.success) {
                console.warn('[AppointmentModal] Failed to notify job thread:', notifyData.error);
              }
            } catch (notifyErr) {
              console.warn('[AppointmentModal] Failed to notify job thread:', notifyErr);
            }
          }

          successCount++;
        } catch (err: unknown) {
          console.error('Error creating shared appointment:', err);
          failCount++;
        }
      }

      if (successCount > 0 && isJobParent) {
        try {
          const jobAssigneesRes = await fetch(`/api/v1/work-items/${selectedJob.id}/assignees`, {
            headers: { 'Authorization': `Bearer ${getAuthToken()}` },
            signal: abortController.signal
          });
          if (jobAssigneesRes.ok) {
            const jobAssData = await jobAssigneesRes.json();
            const existing = (jobAssData.data || []) as { user_id: number }[];

            for (const userId of assigneeUserIds) {
              if (existing.some((assignee) => assignee.user_id === userId)) {
                continue;
              }

              try {
                await fetch(`/api/v1/work-items/${selectedJob.id}/assignees`, {
                  method: 'POST',
                  headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
                  body: JSON.stringify({ user_id: userId }),
                  signal: abortController.signal
                });
              } catch (assigneeError) {
                console.warn('[AppointmentModal] Failed to add assignee to job:', assigneeError);
              }
            }
          }
        } catch (assigneeError) {
          console.warn('[AppointmentModal] Failed to fetch or update job assignees:', assigneeError);
        }

        // Update job status to scheduled when creating a new appointment
        // This handles jobs that are in open, assigned, or resolved/partial status
        if (['open', 'assigned', 'apt_resolved', 'apt_partial', 'resolved', 'partial'].includes(selectedJob.status)) {
          try {
            await fetch(`/api/v1/work-items/${selectedJob.id}`, {
              method: 'PUT',
              headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ status: 'scheduled' }),
              signal: abortController.signal
            });
          } catch (statusError) {
            console.warn('[AppointmentModal] Failed to update job status:', statusError);
          }
        }
      }

      if (successCount > 0) {
        if (recurringOccurrences.length > 1) {
          notify.success(`${successCount} shared appointment(s) created across ${recurringOccurrences.length} occurrence${recurringOccurrences.length === 1 ? '' : 's'}${failCount > 0 ? `, ${failCount} failed` : ''}`);
        } else if (selectedAssignmentTeam || assignees.length > 1) {
          notify.success('Shared appointment created successfully');
        } else if (assignees.length === 1) {
          notify.success('Appointment created successfully');
        } else {
          notify.success(`${successCount} appointment(s) created${failCount > 0 ? `, ${failCount} failed` : ''}`);
        }
        emitOperationsGuidedTourTrainingEvent({
          type: 'appointment_created',
          route: 'appointment_modal',
          workItemId: createdAppointmentIds[0] ? String(createdAppointmentIds[0]) : null,
          itemType: 'appointment',
          metadata: {
            createdCount: successCount,
            failedCount: failCount,
            grouped: false,
            sharedAssigneeCount: assignees.length,
            teamAssigned: Boolean(selectedAssignmentTeam),
            recurring: recurringOccurrences.length > 1,
            recurrenceCount: recurringOccurrences.length,
          },
        });
        onSuccess?.();
        onClose();
        resetForm();
      } else {
        notify.error('Failed to create appointment');
      }
    } catch (error: unknown) {
      console.error('Error creating appointment:', error);
      const appointmentError = error as AppointmentModalError;
      if (appointmentError?.code === 'CREDIT_LIMIT_EXCEEDED') {
        const contactName = appointmentError?.details?.contact_name;
        notify.error(contactName ? `${contactName}: ${appointmentError.message}` : appointmentError.message || 'Credit limit exceeded');
      } else {
        notify.error(getErrorMessage(error) || 'Failed to create appointment');
      }
    } finally {
      clearTimeout(timeoutId);
      abortController.abort();
      setCreating(false);
    }
  };

  const toggleAssignee = (userId: number) => {
    if (isAssignmentLockedToCurrentUser) {
      return;
    }

    setFormData(prev => ({
      ...prev,
      assigned_team: null,
      assigned_to: prev.assigned_to.includes(userId)
        ? prev.assigned_to.filter(id => id !== userId)
        : [...prev.assigned_to, userId]
    }));
  };

  const jobKeyboardNavigation = useKeyboardSelectableList({
    isOpen: !selectedJob && filteredJobs.length > 0,
    items: filteredJobs,
    onSelect: handleJobSelect,
  });

  // Ref for modal scrollable content
  const modalContentRef = useRef<HTMLDivElement>(null);

  // Scroll handler for assignment dropdown
  const handleAssignmentDropdownOpen = useCallback(() => {
    if (modalContentRef.current) {
      // Find the dropdown trigger button and scroll it into view
      const triggerButton = modalContentRef.current.querySelector('button[type="button"]');
      if (triggerButton) {
        // Scroll the trigger button to the top of the modal view
        triggerButton.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        // Fallback to scrolling to top
        modalContentRef.current.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    }
  }, []);

  // Derived location state — used in JSX for button disabled state and display
  const hasCustomLoc = locationSource === 'custom' && !!customLocation;
  const hasContactLoc = locationSource === 'contact' && !!selectedLocation && hasContactLocationCoordinates(selectedLocation);
  const hasValidLocation = hasCustomLoc || hasContactLoc;
  const appointmentSubmitRequirementMessage = [
    !formData.title.trim() ? 'Provide Title' : null,
    !formData.item_subtype ? 'Select Appointment Type' : null,
    !formData.priority ? 'Select Priority' : null,
    !selectedJob ? `Select ${parentLabel}` : null,
    !hasValidLocation ? 'Select Location' : null,
    !hasAssignmentSelection ? 'Assign Resource' : null,
    !formData.scheduled_start ? 'Set Start' : null,
    !formData.scheduled_end ? 'Set End' : null,
    scheduleError ? scheduleError : null,
  ].filter(Boolean).slice(0, scheduleError ? 1 : 4).join(' · ');

  const isAppointmentSubmitDisabled = creating || !formData.title.trim() || !formData.item_subtype || !formData.priority || !selectedJob || !hasValidLocation || !hasAssignmentSelection || !formData.scheduled_start || !formData.scheduled_end || !!scheduleError;

  // Compact header on mobile (Capacitor native app or narrow phone viewport) to match
  // the SmartTask modal header style — drops the subtitle and shrinks the icon/title.
  const isMobileHeader = typeof window !== 'undefined' && (isCapacitor() || window.matchMedia('(max-width: 768px)').matches);

  if (!isOpen) return null;

  const modalContent = (
    <div
      ref={modalContentRef}
      data-tour-id="appointment-modal"
      className="fixed inset-0 z-[130] bg-gray-50 dark:bg-gray-900 overflow-y-auto"
      style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="flex min-h-full flex-col px-2 pb-1 pt-2 sm:px-4 sm:pb-3 sm:pt-4">
        <div className={isMobileHeader ? 'mb-2 px-2 relative' : 'mb-3 sm:mb-6 px-2 relative'}>
          <div className="text-center">
            <div className={isMobileHeader
              ? 'mx-auto mb-1 flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-green-500 to-emerald-600 text-white shadow-lg'
              : 'mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-green-500 to-emerald-600 text-white shadow-lg sm:mb-3 sm:h-16 sm:w-16'}>
              <Calendar className={isMobileHeader ? 'h-5 w-5' : 'h-6 w-6 sm:h-8 sm:w-8'} />
            </div>
            <h1 className={isMobileHeader
              ? 'text-base font-bold text-gray-900 dark:text-white'
              : 'pm-heading-1 text-lg sm:text-2xl mb-1 text-gray-900 dark:text-white'}>New Appointment <UserDocsLink docsPath="/total-operations/workflows/service-operations/appointments#book-an-appointment" label="User guide: Appointments" className="ml-1 align-middle" /></h1>
            {!isMobileHeader && (
              <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                {isAssignmentLockedToCurrentUser ? `Schedule a self-assigned appointment linked to a ${parentLabel.toLowerCase()}` : `Schedule appointment for a ${parentLabel.toLowerCase()}`}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={creating}
            aria-label="Close modal"
            className={`absolute right-2 top-0 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-400 transition-colors hover:border-gray-300 hover:text-gray-600 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-500 dark:hover:border-gray-600 dark:hover:text-gray-300 ${creating ? 'opacity-40 cursor-not-allowed' : ''}`}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="max-w-5xl mx-auto flex-1 p-6 space-y-6">

          {/* ============================================================== */}
          {/* CUSTOMER & LOCATION - 3-Way Toggle Search */}
          {/* ============================================================== */}
          {fromJobContext ? (
            // Compressed view when from job context
            <div className={`rounded-lg p-3 ${isDark ? 'bg-gray-900/50 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
              <div className="flex items-center justify-between mb-2">
                <h3 className={`font-semibold text-sm flex items-center gap-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                  <User className="w-4 h-4 text-blue-600" />
                  Job Context
                </h3>
                <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Inherited from parent job</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {selectedContact && (
                  <div className={`flex items-center gap-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    <Building2 className="w-3 h-3 text-blue-500" />
                    <span className="truncate">{selectedContact.company_name || selectedContact.contact_person}</span>
                  </div>
                )}
                {selectedLocation && (
                  <div className={`flex items-center gap-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    <MapPin className="w-3 h-3 text-green-500" />
                    <span className="truncate">{selectedLocation.location_name}</span>
                  </div>
                )}
                {selectedPerson && (
                  <div className={`flex items-center gap-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    <User className="w-3 h-3 text-purple-500" />
                    <span className="truncate">{selectedPerson.first_name} {selectedPerson.last_name}</span>
                  </div>
                )}
                {selectedJob && (
                  <div className={`flex items-center gap-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    <Briefcase className="w-3 h-3 text-green-600" />
                    <span className="truncate">{selectedJob.item_number}</span>
                  </div>
                )}
              </div>
              {showInheritPeopleToggle && (
                <label className={`mt-2 flex items-center gap-2 text-xs cursor-pointer ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  <input
                    type="checkbox"
                    checked={formData.inherit_all_contact_people}
                    onChange={(e) => setFormData(prev => ({ ...prev, inherit_all_contact_people: e.target.checked }))}
                    className="rounded"
                  />
                  Copy all contact people from the {parentLabel.toLowerCase()} (off = primary only)
                </label>
              )}
            </div>
          ) : (
            // Full view for standalone appointment creation
            <div className={`rounded-lg p-4 ${isDark ? 'bg-gray-900/50 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
              <h3 className={`font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                <User className="w-5 h-5 text-blue-600" />
                Customer & Location
              </h3>

              {selectedJob ? (
                <>
                  <div className={`px-3 py-2 rounded-lg mb-4 text-xs font-medium flex items-center gap-2 ${isDark ? 'bg-green-900/30 border border-green-700/50 text-green-300' : 'bg-green-50 border border-green-200 text-green-700'}`}>
                    <Lock className="w-3.5 h-3.5" />
                    Contact defaults are inherited from the selected {parentLabel.toLowerCase()}. You can still change the appointment location below.
                    <button type="button" onClick={() => { setSelectedJob(null); setFormData(prev => ({ ...prev, title: '', additional_notes: '', equipment_required: '', safety_requirements: '', purchase_items: [] })); }} className={`ml-auto text-xs font-medium ${isDark ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-700'}`}>Change {parentLabel}</button>
                  </div>

                  <div className="grid grid-cols-3 gap-3 mt-4">
                    {/* Contact */}
                    <div data-tour-id="appointment-modal-customer" className={`p-3 rounded-lg border ${selectedContact
                      ? (jobsChecked && availableJobs.length === 0)
                        ? (isDark ? 'bg-red-900/20 border-red-700' : 'bg-red-50 border-red-200')
                        : (isDark ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200')
                      : (isDark ? 'bg-gray-800/50 border-gray-700 border-dashed' : 'bg-gray-50 border-gray-300 border-dashed')
                      }`}>
                      <div className="flex items-center gap-2">
                        <Building2 className={`w-4 h-4 ${selectedContact
                          ? (jobsChecked && availableJobs.length === 0) ? 'text-red-500' : 'text-blue-500'
                          : 'text-gray-400'
                          }`} />
                        <div className="flex-1 min-w-0">
                          <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Contact *</div>
                          <div className={`text-sm font-medium truncate ${selectedContact ? (isDark ? 'text-gray-200' : 'text-gray-800') : 'text-gray-400'}`}>
                            {selectedContact ? (selectedContact.company_name || selectedContact.contact_person) : 'Not selected'}
                          </div>
                          {selectedContact && jobsChecked && availableJobs.length === 0 && (
                            <div className={`text-[10px] mt-0.5 font-medium ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                              ⚠ No open {parentLabelPlural}
                            </div>
                          )}
                        </div>
                        {selectedContact && !selectedJob && <button type="button" onClick={() => { setSelectedContact(null); setSelectedLocation(null); setSelectedJob(null); setLocationSource('contact'); setCustomLocation(null); setCustomLocSearch(''); setJobsChecked(false); }} className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"><X className="w-3 h-3 text-gray-400" /></button>}
                      </div>
                    </div>
                    {/* Location */}
                    <div data-tour-id="appointment-modal-contact-location" className={`p-3 rounded-lg border ${hasValidLocation
                      ? (isDark ? 'bg-green-900/20 border-green-700' : 'bg-green-50 border-green-200')
                      : (isDark ? 'bg-gray-800/50 border-gray-700 border-dashed' : 'bg-gray-50 border-gray-300 border-dashed')
                      }`}>
                      <div className="flex items-center gap-2">
                        <MapPin className={`w-4 h-4 ${hasValidLocation ? 'text-green-500' : 'text-gray-400'}`} />
                        <div className="flex-1 min-w-0">
                          <div className={`text-xs flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            Location *
                            {hasCustomLoc && (
                              <span className="px-1 text-[10px] bg-orange-500/20 text-orange-500 rounded font-medium">custom</span>
                            )}
                          </div>
                          {hasCustomLoc ? (
                            <div className={`text-sm font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{customLocation!.name}</div>
                          ) : hasContactLoc ? (
                            <div className={`text-sm font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{selectedLocation!.location_name}</div>
                          ) : selectedContact ? (
                            locationSource === 'contact' && locations.length > 0 ? (
                              <div className="flex items-center gap-1">
                                <select value={selectedLocation?.id || ''} onChange={(e) => { const loc = locations.find(l => Number(l.id) === Number(e.target.value)); setSelectedLocation(loc || null); }}
                                  className={`flex-1 text-xs py-1 px-1 rounded border ${isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'}`}>
                                  <option value="">Select...</option>
                                  {locations.map(loc => <option key={loc.id} value={loc.id}>{loc.location_name}{loc.is_primary ? ' ★' : ''}</option>)}
                                </select>
                                <button type="button" onClick={() => setShowLocationCreate(true)} title="Create new location"
                                  className={`p-1 rounded transition-colors ${isDark ? 'text-gray-400 hover:text-blue-400 hover:bg-gray-700' : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'}`}>
                                  <Plus className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            ) : (
                              <div className={`text-xs italic ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                {locationSource === 'custom' ? 'Set below ↓' : 'Set below ↓'}
                              </div>
                            )
                          ) : (
                            <div className="text-sm text-gray-400">Select contact first</div>
                          )}
                        </div>
                        {hasCustomLoc && !selectedJob && (
                          <button type="button" onClick={() => { setCustomLocation(null); setCustomLocSearch(''); }}
                            className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"><X className="w-3 h-3 text-gray-400" /></button>
                        )}
                        {hasContactLoc && !selectedJob && (
                          <button type="button" onClick={() => setSelectedLocation(null)}
                            className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"><X className="w-3 h-3 text-gray-400" /></button>
                        )}
                      </div>
                      {/* Create location form (contact mode only) */}
                      {showLocationCreate && selectedContact && locationSource === 'contact' && (
                        <QuickLocationForm
                          contactId={selectedContact.id}
                          onCreated={(newLoc: CreatedLocation) => {
                            const mapped: Location = {
                              id: newLoc.id,
                              contact_id: newLoc.contact_id,
                              location_name: newLoc.location_name,
                              address_line1: newLoc.address_line1,
                              city: newLoc.city,
                              is_primary: newLoc.is_primary,
                            };
                            const cachedLocations = contactLocationsCacheRef.current.get(mapped.contact_id) || [];
                            contactLocationsCacheRef.current.set(
                              mapped.contact_id,
                              [...cachedLocations.filter((location) => Number(location.id) !== Number(mapped.id)), mapped]
                            );
                            setLocations(prev => [...prev, mapped]);
                            setSelectedLocation(mapped);
                            setLocationLoadError(null);
                            setShowLocationCreate(false);
                          }}
                          onCancel={() => setShowLocationCreate(false)}
                        />
                      )}
                    </div>
                    {/* Contact Person */}
                    <div data-tour-id="appointment-modal-contact-person" className={`p-3 rounded-lg border ${selectedPerson ? (isDark ? 'bg-purple-900/20 border-purple-700' : 'bg-purple-50 border-purple-200') : (isDark ? 'bg-gray-800/50 border-gray-700 border-dashed' : 'bg-gray-50 border-gray-300 border-dashed')}`}>
                      <div className="flex items-center gap-2">
                        <User className={`w-4 h-4 ${selectedPerson ? 'text-purple-500' : 'text-gray-400'}`} />
                        <div className="flex-1 min-w-0">
                          <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Contact Person</div>
                          {selectedPerson ? (
                            <div className={`text-sm font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{selectedPerson.first_name} {selectedPerson.last_name}</div>
                          ) : selectedContact ? (
                            loadingPeople ? (
                              <div className="flex items-center gap-1 text-xs text-gray-500"><Loader2 className="w-3 h-3 animate-spin" />Loading...</div>
                            ) : <select value={''} onChange={(e) => { const p = contactPeople.find(cp => Number(cp.id) === Number(e.target.value)); setSelectedPerson(p || null); }}
                              className={`w-full text-xs py-1 px-1 rounded border ${isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'}`}>
                              <option value="">Optional...</option>
                              {contactPeople.map(p => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}{p.job_title ? ` (${p.job_title})` : ''}</option>)}
                            </select>
                          ) : (
                            <div className="text-sm text-gray-400">Optional</div>
                          )}
                        </div>
                        {selectedPerson && !selectedJob && <button type="button" onClick={() => setSelectedPerson(null)} className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"><X className="w-3 h-3 text-gray-400" /></button>}
                      </div>
                    </div>
                  </div>
                  {showInheritPeopleToggle && (
                    <label className={`mt-2 flex items-center gap-2 text-xs cursor-pointer ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      <input
                        type="checkbox"
                        checked={formData.inherit_all_contact_people}
                        onChange={(e) => setFormData(prev => ({ ...prev, inherit_all_contact_people: e.target.checked }))}
                        className="rounded"
                      />
                      Copy all contact people from the {parentLabel.toLowerCase()} (off = primary only)
                    </label>
                  )}
                </>
              ) : (
                <>
                  <UnifiedContactLookup
                    contacts={contacts}
                    locations={sharedLocations}
                    contactPeople={sharedContactPeople}
                    selectedContactId={selectedContact?.id ?? null}
                    selectedLocationId={selectedLocation?.id ?? null}
                    selectedContactPersonId={selectedPerson?.id ?? null}
                    selectedContactPersonName={selectedPerson ? `${selectedPerson.first_name} ${selectedPerson.last_name}`.trim() : ''}
                    onContactChange={handleSharedContactChange}
                    onLocationChange={handleSharedLocationChange}
                    onContactPersonChange={handleSharedContactPersonChange}
                    onUnifiedSelect={handleUnifiedSelect}
                    onLocationCreated={handleSharedLocationCreated}
                    onPersonCreated={handleSharedPersonCreated}
                    loadingContacts={loadingContacts}
                    loadingLocations={loadingLocations}
                    loadingPeople={loadingPeople}
                    isDark={isDark}
                    contactTypes={['customer']}
                    onViewStakeholder={selectedContact ? () => setShowStakeholderOverview(true) : undefined}
                  />

                  {selectedContact && jobsChecked && availableJobs.length === 0 && (
                    <div className={`mt-4 rounded-lg px-3 py-2 text-xs font-medium flex items-center gap-2 ${isDark ? 'bg-red-900/20 border border-red-700/50 text-red-300' : 'bg-red-50 border border-red-200 text-red-700'}`}>
                      <AlertTriangle className="w-3.5 h-3.5" />
                      No open jobs found for this contact yet. Create one below to continue.
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ============================================================== */}
          {/* JOB SELECTION */}
          {/* ============================================================== */}
          {!fromJobContext && selectedContact && (
            <div data-tour-id="appointment-modal-job" className={`rounded-lg p-4 ${isDark ? 'bg-green-900/30 border border-green-700' : 'bg-green-50 border border-green-200'}`}>
              <h3 className={`font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                <Briefcase className="w-5 h-5 text-green-600" />
                Link to {parentLabel} <span className="text-red-500">*</span>
              </h3>

              {selectedJob ? (
                <div className={`flex items-center justify-between gap-2 py-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <Briefcase className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <div className="min-w-0">
                      <span className={`font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{selectedJob.item_number}</span>
                      <span className={`mx-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>•</span>
                      <span className={`whitespace-normal break-words ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{selectedJob.title}</span>
                      {selectedJob.manager_name && (
                        <span className={`block text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          Manager: {selectedJob.manager_name} (inherited)
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setSelectedJob(null); setFormData(prev => ({ ...prev, title: '', additional_notes: '', equipment_required: '', safety_requirements: '', purchase_items: [] })); }}
                    className={`text-xs font-medium px-2 py-1 rounded transition-colors ${isDark ? 'text-red-400 hover:bg-red-900/30' : 'text-red-600 hover:bg-red-50'}`}
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div className="relative" ref={jobDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setIsJobDropdownOpen(!isJobDropdownOpen)}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border transition-colors ${isDark
                      ? 'bg-gray-700 border-gray-600 text-gray-200 hover:border-gray-500'
                      : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
                      }`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Briefcase className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className={`truncate ${!selectedJob ? 'text-gray-400' : ''}`}>
                        {(selectedJob as JobSearchResult | null)?.item_number ?? `Select ${parentLabel.toLowerCase()}...`}
                      </span>
                    </div>
                    <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform ${isJobDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {isJobDropdownOpen && (
                    <div className={`absolute z-50 w-full mt-1 rounded-lg shadow-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                      }`}>
                      <div className={`p-2 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                        <div className="relative">
                          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="text"
                            value={jobSearchQuery}
                            onChange={(e) => setJobSearchQuery(e.target.value)}
                            onKeyDown={jobKeyboardNavigation.handleKeyDown}
                            placeholder={`Search ${parentLabelPlural}...`}
                            className={`w-full pl-8 pr-3 py-1.5 text-sm rounded border ${isDark
                              ? 'bg-gray-700 border-gray-600 text-gray-200'
                              : 'bg-gray-50 border-gray-200 text-gray-700'
                              }`}
                            role="combobox"
                            aria-expanded={isJobDropdownOpen}
                            aria-controls="appointment-modal-job-results"
                            aria-activedescendant={jobKeyboardNavigation.activeIndex >= 0 ? `appointment-modal-job-option-${filteredJobs[jobKeyboardNavigation.activeIndex]?.id}` : undefined}
                            autoFocus
                          />
                        </div>
                      </div>

                      <div id="appointment-modal-job-results" role="listbox" className="max-h-48 overflow-y-auto">
                        {searchingJobs ? (
                          <div className="p-3 text-center text-gray-500 text-sm"><Loader2 className="w-4 h-4 animate-spin mx-auto mb-1" />Loading {parentLabelPlural}...</div>
                        ) : filteredJobs.length === 0 ? (
                          availableJobs.length === 0 && jobsChecked ? (
                            <div className={`p-4 text-center ${isDark ? 'bg-red-900/20' : 'bg-red-50'}`}>
                              <AlertTriangle className={`w-6 h-6 mx-auto mb-2 ${isDark ? 'text-red-400' : 'text-red-500'}`} />
                              <p className={`text-sm font-semibold mb-1 ${isDark ? 'text-red-300' : 'text-red-700'}`}>
                                No open {parentLabelPlural} for {selectedContact?.company_name || selectedContact?.contact_person || 'this contact'}
                              </p>
                              <p className={`text-xs mb-3 ${isDark ? 'text-red-400/80' : 'text-red-600/80'}`}>
                                An appointment must be linked to an active {parentLabel.toLowerCase()}.
                              </p>
                            </div>
                          ) : (
                            <div className="p-3 text-center text-gray-500 text-sm">
                              No matching {parentLabelPlural}
                            </div>
                          )
                        ) : (
                          filteredJobs.map((job, index) => (
                            <button
                              key={job.id}
                              type="button"
                              onClick={() => handleJobSelect(job)}
                              ref={jobKeyboardNavigation.registerItemRef(index)}
                              onMouseEnter={() => jobKeyboardNavigation.setActiveIndex(index)}
                              id={`appointment-modal-job-option-${job.id}`}
                              role="option"
                              aria-selected={jobKeyboardNavigation.activeIndex === index}
                              className={`w-full flex items-center gap-2 p-2 text-left transition-colors ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                                } ${jobKeyboardNavigation.activeIndex === index ? (isDark ? 'bg-gray-700' : 'bg-blue-50') : ''}`}
                            >
                              <Briefcase className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className={`text-sm font-medium whitespace-normal break-words ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                                  {job.item_number}
                                </div>
                                <div className={`text-sm whitespace-normal break-words ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{job.title}</div>
                                {job.location_name && <div className={`text-xs flex items-center gap-1 mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}><MapPin className="w-3 h-3" />{job.location_name}</div>}
                              </div>
                            </button>
                          ))
                        )}
                      </div>

                      {allowParentCreate && (
                        <div className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                          <button
                            type="button"
                            onClick={() => {
                              setIsJobDropdownOpen(false);
                              setShowJobCreateForm(true);
                            }}
                            className={`w-full flex items-center gap-2 p-2 text-left text-xs font-medium ${isDark ? 'text-blue-400 hover:bg-gray-700' : 'text-blue-600 hover:bg-blue-50'}`}
                          >
                            <Plus className="w-3.5 h-3.5" /> Create New {parentLabel}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {showJobCreateForm && allowParentCreate && (
                <div className={`mt-3 rounded-lg border p-3 ${isDark ? 'bg-gray-800/80 border-green-700/60' : 'bg-white border-green-200'}`}>
                  <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                    Job Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={newJobDescription}
                    onChange={(e) => setNewJobDescription(e.target.value)}
                    rows={4}
                    placeholder="Describe the service work needed for this contact..."
                    className={`w-full px-3 py-2 border rounded-lg resize-y focus:ring-2 focus:ring-green-500 ${isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'}`}
                  />
                  <p className={`mt-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    We&apos;ll create the job for the selected contact and use the first line of this description as the job title.
                  </p>
                  <div className="mt-3 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        setShowJobCreateForm(false);
                        setNewJobDescription('');
                      }}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}`}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleCreateJob}
                      disabled={creatingJob}
                      className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-green-600 hover:bg-green-700 text-white disabled:opacity-60"
                    >
                      {creatingJob ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Creating job...
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4" />
                          Create Job
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ============================================================== */}
          {/* APPOINTMENT DETAILS — disabled until contact + job selected */}
          {/* ============================================================== */}
          {!selectedContact || !selectedJob ? (
            <div className={`rounded-lg p-6 border-2 border-dashed text-center ${isDark ? 'bg-gray-900/30 border-gray-700' : 'bg-gray-50 border-gray-300'}`}>
              <Lock className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
              <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {!selectedContact ? 'Select a customer above to continue' : `Select a ${parentLabel.toLowerCase()} to link this appointment to`}
              </p>
              <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Appointment details, scheduling, and team assignment will be available after selection.
              </p>
            </div>
          ) : (
            <>
              <div data-tour-id="appointment-modal-title" className="grid grid-cols-3 gap-4">
                <div className="col-span-2">
                  <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Title <span className="text-red-500">*</span></label>
                  <input type="text" value={formData.title} onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 ${isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'}`}
                    placeholder="Enter appointment title" />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Type <span className="text-red-500">*</span></label>
                  <select value={formData.item_subtype} onChange={(e) => setFormData(prev => ({ ...prev, item_subtype: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}>
                    <option value="">Select type...</option>
                    {appointmentTypeOptions.map(type => (
                      <option key={type.key} value={type.key}>{type.label} {type.visible_to_customer !== false ? '🟢 Visible to customer' : '🔴 Not visible to customer'}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Priority */}
              <div data-tour-id="appointment-modal-priority" className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Priority <span className="text-red-500">*</span></label>
                  <select value={formData.priority} onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}>
                    <option value="">Select priority...</option>
                    {APPOINTMENT_PRIORITIES.map(p => (
                      <option key={p.value} value={p.value}>{p.icon} {p.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* ==============================================================*/}
              {/* APPOINTMENT LOCATION — inherit from contact, other contact, or custom */}
              {/* ==============================================================*/}
              {selectedContact && (
                <div data-tour-id="appointment-modal-location">
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span>Appointment Location <span className="text-red-500">*</span></span>
                    </div>
                  </label>

                  {/* 3-mode toggle: Contact Location | Other Contact | Custom */}
                  <div className={`flex p-1 rounded-lg mb-3 gap-1 ${isDark ? 'bg-gray-700/60' : 'bg-gray-100'}`}>
                    <button
                      type="button"
                      onClick={() => { setLocationSource('contact'); setCustomLocation(null); setCustomLocSearch(''); }}
                      className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium rounded-md transition-all ${locationSource === 'contact'
                        ? (isDark ? 'bg-gray-600 text-white shadow-sm' : 'bg-white text-gray-800 shadow-sm')
                        : (isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700')
                        }`}
                    >
                      <Building2 className="w-3 h-3" />
                      Contact Location
                    </button>
                    <button
                      type="button"
                      onClick={() => { setLocationSource('custom'); setSelectedLocation(null); }}
                      className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium rounded-md transition-all ${locationSource === 'custom'
                        ? (isDark ? 'bg-orange-600 text-white shadow-sm' : 'bg-orange-500 text-white shadow-sm')
                        : (isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700')
                        }`}
                    >
                      <MapPin className="w-3 h-3" />
                      Custom
                    </button>
                  </div>

                  {/* CONTACT LOCATION MODE — pick from selected contact's locations */}
                  {locationSource === 'contact' && (
                    <>
                      {loadingLocations ? (
                        <div className={`w-full px-3 py-2 border rounded-lg flex items-center gap-2 text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-gray-400' : 'bg-gray-50 border-gray-300 text-gray-500'}`}>
                          <Loader2 className="w-4 h-4 animate-spin" /> Loading locations...
                        </div>
                      ) : locationLoadError ? (
                        <div className={`w-full px-3 py-2 border rounded-lg text-sm space-y-2 ${isDark ? 'bg-red-900/20 border-red-700 text-red-200' : 'bg-red-50 border-red-200 text-red-700'}`}>
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <span>{locationLoadError}</span>
                          </div>
                          <div className="flex flex-wrap items-center gap-3 text-xs font-medium">
                            <button
                              type="button"
                              onClick={() => { if (selectedContactId) { void fetchLocationsForContact(selectedContactId, { forceRefresh: true }); } }}
                              className={`${isDark ? 'text-red-200 hover:text-white' : 'text-red-700 hover:text-red-800'}`}
                            >
                              Retry loading
                            </button>
                            <button
                              type="button"
                              onClick={() => setLocationSource('custom')}
                              className={`${isDark ? 'text-orange-300 hover:text-orange-200' : 'text-orange-600 hover:text-orange-700'}`}
                            >
                              Use custom instead
                            </button>
                          </div>
                        </div>
                      ) : locations.length === 0 ? (
                        <div className={`w-full px-3 py-2 border rounded-lg text-sm flex items-center justify-between gap-2 ${isDark ? 'bg-gray-700 border-gray-600 text-gray-400' : 'bg-gray-50 border-gray-300 text-gray-500'}`}>
                          <span>No locations on file for this contact</span>
                          <button type="button" onClick={() => setLocationSource('custom')}
                            className="text-xs text-orange-500 hover:text-orange-600 font-medium whitespace-nowrap flex items-center gap-1">
                            <MapPin className="w-3 h-3" />Use custom instead
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {selectedJob && selectedLocation && (
                            <p className={`text-xs ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                              Pre-selected from job — you can change it below
                            </p>
                          )}
                          <select
                            value={selectedLocation?.id || ''}
                            onChange={(e) => { const loc = locations.find(l => Number(l.id) === Number(e.target.value)); setSelectedLocation(loc || null); }}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                          >
                            <option value="">Select location...</option>
                            {locations.map(loc => (
                              <option key={loc.id} value={loc.id}>
                                {loc.location_name}{loc.is_primary ? ' \u2605' : ''}{loc.address_line1 ? ` \u2014 ${loc.address_line1}` : ''}{loc.city ? `, ${loc.city}` : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </>
                  )}

                  {/* CUSTOM LOCATION MODE — Google Places search */}
                  {locationSource === 'custom' && (
                    <div className="space-y-3">
                      {/* Info banner */}
                      <div className={`flex items-start gap-2 px-3 py-2 rounded-lg text-xs ${isDark ? 'bg-orange-900/20 border border-orange-700/50 text-orange-300' : 'bg-orange-50 border border-orange-200 text-orange-700'
                        }`}>
                        <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                        <span>This location is stored only on this appointment and will <strong>not</strong> be saved to the contact&apos;s locations.</span>
                      </div>

                      {/* Google Places Autocomplete */}
                      <LocationAutocomplete
                        value={customLocSearch}
                        onChange={setCustomLocSearch}
                        onPlaceSelect={(data) => {
                          setCustomLocation(data);
                          setCustomLocSearch(data.name);
                        }}
                        onClear={() => { setCustomLocSearch(''); setCustomLocation(null); }}
                        isDark={isDark}
                      />

                      {/* Selected custom location display */}
                      {customLocation && (
                        <CustomLocationResult
                          location={customLocation}
                          onClear={() => { setCustomLocation(null); setCustomLocSearch(''); }}
                          isDark={isDark}
                        />
                      )}

                      {!customLocation && (
                        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          Start typing to search — select a result from the map search dropdown to capture coordinates.
                        </p>
                      )}

                      {/* Contact Person for custom location */}
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          Contact Person at Location
                        </label>
                        <input
                          type="text"
                          value={formData.custom_contact_person}
                          onChange={(e) => setFormData(prev => ({ ...prev, custom_contact_person: e.target.value }))}
                          placeholder="e.g. John Smith — site manager"
                          className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-green-500 ${isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'}`}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ============================================================== */}
              {/* ASSIGN TEAM MEMBER(S) - moved ABOVE schedule */}
              {/* Each team member gets a separate appointment */}
              {/* ============================================================== */}
              <div data-tour-id="appointment-modal-assignees">
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    <span>Assign Team Member(s) <span className="text-red-500">*</span></span>
                  </div>
                  <p className={`text-xs mt-1 font-normal ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    {isAssignmentLockedToCurrentUser
                      ? 'Appointments created from Smart Task are assigned to you automatically.'
                      : 'Each selected member will get their own individual appointment booking.'}
                  </p>
                </label>
                {isAssignmentLockedToCurrentUser && lockedAssignee ? (
                  <div className={`rounded-xl border p-4 ${isDark ? 'border-green-700 bg-green-900/20' : 'border-green-200 bg-green-50'}`}>
                    <div className="flex items-center gap-3">
                      <div className={`flex h-10 w-10 items-center justify-center rounded-full ${isDark ? 'bg-green-800 text-green-200' : 'bg-green-100 text-green-700'}`}>
                        <Lock className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                          Assigned to you
                        </div>
                        <div className={`truncate text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          {lockedAssignee.name || `${lockedAssignee.first_name || ''} ${lockedAssignee.last_name || ''}`.trim() || lockedAssignee.email || 'Current user'}
                        </div>
                        {lockedAssignee.email && (
                          <div className={`truncate text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {lockedAssignee.email}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <TeamMemberMultiSelect
                    selectedIds={formData.assigned_to}
                    teamMembers={effectiveTechnicians}
                    onToggle={toggleAssignee}
                    loading={loadingTechnicians}
                    isDark={isDark}
                    availability={availability}
                    loadingAvailability={loadingAvailability}
                    onDropdownOpen={handleAssignmentDropdownOpen}
                  />
                )}
              </div>

              {!hasAssignmentSelection ? (
                <div className={`rounded-lg border-2 border-dashed p-6 text-center ${isDark ? 'border-gray-700 bg-gray-900/30' : 'border-gray-300 bg-gray-50'}`}>
                  <Users className={`mx-auto mb-2 h-8 w-8 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
                  <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    Select at least one team member to continue
                  </p>
                  <p className={`mt-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Scheduling, recurrence, and requirements stay hidden until the appointment has an owner.
                  </p>
                </div>
              ) : (
                <>
                  <div data-tour-id="appointment-modal-schedule">
                    <label className={`block text-sm font-medium mb-1.5 flex items-center gap-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      <Clock className="w-4 h-4" />Schedule <span className="text-red-500">*</span>
                    </label>
                    <AppointmentScheduler
                      scheduledStart={formData.scheduled_start}
                      scheduledEnd={formData.scheduled_end}
                      onChange={(next) => setFormData(prev => ({
                        ...prev,
                        scheduled_start: next.scheduled_start,
                        scheduled_end: next.scheduled_end,
                      }))}
                      isDark={isDark}
                    />
                    <div className={`mt-2 text-xs ${scheduleError ? (isDark ? 'text-red-300' : 'text-red-600') : (isDark ? 'text-gray-500' : 'text-gray-500')}`}>
                      {scheduleError || 'Appointments default to 1 hour and cannot exceed 24 hours.'}
                    </div>
                  </div>

                  <div className={`rounded-lg border p-4 space-y-3 ${isDark ? 'border-gray-700 bg-gray-900/40' : 'border-gray-200 bg-gray-50/80'}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <label className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Recurring appointments</label>
                        <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                          Create a repeating series from this first appointment slot.
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={recurrenceRule.enabled}
                          onChange={(e) => setRecurrenceRule(prev => ({ ...prev, enabled: e.target.checked }))}
                          className="sr-only peer"
                        />
                        <div className={`w-11 h-6 rounded-full ${recurrenceRule.enabled ? 'bg-green-600' : (isDark ? 'bg-gray-600' : 'bg-gray-300')}`}>
                          <div className={`absolute top-[2px] left-[2px] w-5 h-5 rounded-full bg-white transition-transform ${recurrenceRule.enabled ? 'translate-x-5' : ''}`} />
                        </div>
                      </label>
                    </div>

                    {recurrenceRule.enabled && (
                      <div className="grid gap-3 sm:grid-cols-3">
                        <div>
                          <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Frequency</label>
                          <select
                            value={recurrenceRule.frequency}
                            onChange={(e) => setRecurrenceRule(prev => ({ ...prev, frequency: e.target.value as AppointmentRecurrenceRule['frequency'] }))}
                            className={`w-full px-3 py-2 border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                          >
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="fortnightly">Fortnightly</option>
                            <option value="monthly">Monthly</option>
                          </select>
                        </div>
                        <div>
                          <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Every</label>
                          <input
                            type="number"
                            min="1"
                            max="12"
                            value={recurrenceRule.intervalCount}
                            onChange={(e) => setRecurrenceRule(prev => ({ ...prev, intervalCount: Math.max(1, parseInt(e.target.value, 10) || 1) }))}
                            className={`w-full px-3 py-2 border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                          />
                        </div>
                        <div>
                          <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Occurrences</label>
                          <input
                            type="number"
                            min="2"
                            max="52"
                            value={recurrenceRule.occurrenceCount}
                            onChange={(e) => setRecurrenceRule(prev => ({ ...prev, occurrenceCount: Math.max(2, Math.min(52, parseInt(e.target.value, 10) || 2)) }))}
                            className={`w-full px-3 py-2 border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                          />
                        </div>
                      </div>
                    )}

                    {recurrenceRule.enabled && recurringOccurrences.length > 1 && (
                      <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        This will create {recurringOccurrences.length} appointment slots and {recurringOccurrences.length * Math.max(1, formData.assigned_team ? 1 : selectedAssignmentUserIds.length)} total appointment records for the current assignment.
                      </div>
                    )}
                  </div>

                  {selectedAssignmentUserIds.length > 0 && selectedDateStr && (
                    <div className={`rounded-lg border p-4 ${isDark ? 'bg-gray-900/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                      <div className={`text-sm font-medium mb-3 flex items-center gap-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                        <Calendar className="w-4 h-4" />
                        Day Schedule — {new Date(selectedDateStr + 'T00:00:00').toLocaleDateString('en-ZA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: SAST_TIMEZONE })}
                      </div>
                      {loadingAvailability ? (
                        <div className={`flex items-center gap-2 text-sm py-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          <Loader2 className="w-4 h-4 animate-spin" /> Loading schedules...
                        </div>
                      ) : (
                        <div>
                          {selectedAssignmentUserIds.map(userId => {
                            const member = effectiveTechnicians.find(t => t.id === userId);
                            const memberName = member
                              ? `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email?.split('@')[0] || 'User'
                              : `User #${userId}`;
                            const memberAppts = availability[userId] || [];
                            return (
                              <DayScheduleTimeline
                                key={userId}
                                memberName={memberName}
                                appointments={memberAppts}
                                proposedStart={formData.scheduled_start}
                                proposedEnd={formData.scheduled_end}
                                isDark={isDark}
                              />
                            );
                          })}
                          <div className={`flex items-center gap-4 mt-3 pt-2 border-t text-[11px] ${isDark ? 'border-gray-700 text-gray-500' : 'border-gray-200 text-gray-400'}`}>
                            <div className="flex items-center gap-1.5">
                              <div className={`w-3 h-2.5 rounded-sm ${isDark ? 'bg-amber-700/70 border border-amber-600' : 'bg-amber-200 border border-amber-300'}`} />
                              <span>Existing</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <div className={`w-3 h-2.5 rounded-sm border-2 border-dashed ${isDark ? 'bg-green-500/25 border-green-400' : 'bg-green-500/20 border-green-500'}`} />
                              <span>Proposed</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <div className="w-3 h-2.5 rounded-sm bg-red-500/20 border-2 border-dashed border-red-500" />
                              <span>Conflict</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className={`rounded-lg p-4 space-y-4 ${isDark ? 'bg-green-900/20 border border-green-700/50' : 'bg-green-50 border border-green-200'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Target className="w-4 h-4 text-green-600" />
                          <span className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>Require Location Verification</span>
                          {selectedJob && (
                            <span className={`text-xs px-1.5 py-0.5 rounded ${isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'}`}>
                              Inherited from job
                            </span>
                          )}
                        </div>
                        <p className={`text-xs mt-0.5 ml-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Worker must be at job site to check in</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" checked={formData.require_location_verification} onChange={(e) => setFormData(prev => ({ ...prev, require_location_verification: e.target.checked }))} className="sr-only peer" />
                        <div className={`w-11 h-6 rounded-full peer peer-focus:ring-2 peer-focus:ring-blue-300 ${formData.require_location_verification ? 'bg-blue-600' : 'bg-gray-300'}`}>
                          <div className={`absolute top-[2px] left-[2px] w-5 h-5 bg-white rounded-full transition-transform ${formData.require_location_verification ? 'translate-x-5' : 'translate-x-0'}`} />
                        </div>
                      </label>
                    </div>
                  </div>

                  {isPurchaseAppointmentType ? (
                    <PurchaseItemList
                      ref={purchaseItemListRef}
                      items={purchaseItems}
                      onChange={(nextItems) => setFormData(prev => ({ ...prev, purchase_items: nextItems }))}
                      isDark={isDark}
                      mode="edit"
                      title="Itemized Purchase List"
                      helperText="Capture the expected shopping list for this purchase appointment. Use numbered quick capture or speech-to-text to add the next item fast."
                    />
                  ) : null}

                  {isExpenseRequiredAppointmentType && isSupplierContact && (
                    <div className="space-y-3">
                      <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        Attach Purchase Orders <span className={`text-xs font-normal ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>(optional)</span>
                      </label>

                      {selectedPurchaseOrders.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {selectedPurchaseOrders.map((po) => (
                            <div
                              key={po.id}
                              className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-sm ${isDark ? 'border-gray-700 bg-gray-800 text-gray-200' : 'border-gray-200 bg-white text-gray-900'}`}
                            >
                              <Package className="h-3.5 w-3.5 text-gray-400" />
                              <span className="font-medium">{po.item_number}</span>
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedPurchaseOrders((prev) => prev.filter((p) => p.id !== po.id));
                                }}
                                className={`ml-1 rounded-full p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="relative" ref={poDropdownRef}>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            placeholder="Search open purchase orders..."
                            value={poSearchQuery}
                            onChange={(e) => {
                              setPoSearchQuery(e.target.value);
                              setShowPOSearch(true);
                            }}
                            onFocus={() => setShowPOSearch(true)}
                            className={`w-full rounded-lg border py-2.5 pl-9 pr-3 text-sm shadow-sm transition focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500 ${isDark ? 'border-gray-600 bg-gray-700 text-white placeholder-gray-400' : 'border-gray-300 bg-white text-gray-900'}`}
                          />
                        </div>
                        {showPOSearch && (
                          <div className={`absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg border shadow-lg ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
                            {isSearchingPO ? (
                              <div className="p-4 text-center text-sm text-gray-500">
                                <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin" />
                                Searching...
                              </div>
                            ) : poSearchResults.length === 0 ? (
                              <div className="p-4 text-center text-sm text-gray-500">
                                {poSearchQuery.trim() ? 'No open purchase orders found' : 'Type to search open purchase orders'}
                              </div>
                            ) : (
                              <div>
                                {poSearchResults.map((po) => {
                                  const isSelected = selectedPurchaseOrders.some((p) => p.id === po.id);
                                  return (
                                    <button
                                      key={po.id}
                                      type="button"
                                      onClick={() => {
                                        setSelectedPurchaseOrders((prev) =>
                                          isSelected ? prev.filter((p) => p.id !== po.id) : [...prev, po]
                                        );
                                      }}
                                      className={`flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 ${isSelected ? 'bg-green-50 dark:bg-green-900/20' : ''}`}
                                    >
                                      <div className={`flex h-5 w-5 items-center justify-center rounded border ${isSelected ? 'border-green-500 bg-green-500 text-white' : 'border-gray-300 dark:border-gray-600'}`}>
                                        {isSelected && <Check className="h-3.5 w-3.5" />}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="font-medium text-gray-900 dark:text-white truncate">
                                          {po.item_number}
                                        </div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                          {po.title}
                                        </div>
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <OptionalAccordionSection
                    title="Requirements"
                    description="Equipment, safety requirements, and extra operational notes for the team."
                    icon={<Briefcase className="h-4 w-4" />}
                    isOpen={expandedRequirements}
                    onToggle={() => setExpandedRequirements(prev => !prev)}
                    isDark={isDark}
                  >
                    <div className="space-y-4">
                      {!isPurchaseAppointmentType && (
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <div>
                            <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Equipment Required</label>
                            <textarea value={formData.equipment_required} onChange={(e) => setFormData(prev => ({ ...prev, equipment_required: e.target.value }))} rows={2}
                              className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-green-500 ${isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'}`}
                              placeholder="e.g. Ladder, drill, multimeter..." />
                            {selectedJob?.custom_fields?.equipment_required && formData.equipment_required === selectedJob.custom_fields.equipment_required && (
                              <p className={`text-xs mt-1 ${isDark ? 'text-green-400' : 'text-green-600'}`}>Inherited from job</p>
                            )}
                          </div>
                          <div>
                            <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>PPE / Safety Requirements</label>
                            <textarea value={formData.safety_requirements} onChange={(e) => setFormData(prev => ({ ...prev, safety_requirements: e.target.value }))} rows={2}
                              className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-green-500 ${isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'}`}
                              placeholder="e.g. Hard hat, safety glasses, gloves..." />
                            {selectedJob?.custom_fields?.safety_requirements && formData.safety_requirements === selectedJob.custom_fields.safety_requirements && (
                              <p className={`text-xs mt-1 ${isDark ? 'text-green-400' : 'text-green-600'}`}>Inherited from job</p>
                            )}
                          </div>
                        </div>
                      )}

                      <div>
                        <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Additional Notes</label>
                        <textarea value={formData.additional_notes} onChange={(e) => setFormData(prev => ({ ...prev, additional_notes: e.target.value }))} rows={3}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 ${isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'}`}
                          placeholder={`Inherited additional notes from the ${parentLabel.toLowerCase()}...`} />
                        {(selectedJob?.notes || selectedJob?.custom_fields?.additional_notes) && formData.additional_notes === (selectedJob?.notes || selectedJob?.custom_fields?.additional_notes) && (
                          <p className={`text-xs mt-1 ${isDark ? 'text-green-400' : 'text-green-600'}`}>Inherited from job</p>
                        )}
                      </div>
                    </div>
                  </OptionalAccordionSection>

                  <div data-tour-id="appointment-modal-notes">
                    <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Notes</label>
                    <textarea value={formData.description} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} rows={3}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 ${isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'}`}
                      placeholder="Add any special instructions or notes..." />
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <ModalFooter
          message={!creating && isAppointmentSubmitDisabled ? appointmentSubmitRequirementMessage : undefined}
        >
          <button
            onClick={onClose}
            disabled={creating}
            className={`w-full sm:w-auto px-4 py-2 rounded-lg transition-colors ${creating ? 'opacity-40 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700'}`}
          >
            Cancel
          </button>
          <button
            data-tour-id={fromJobContext ? 'appointments-tab-submit' : 'appointment-modal-submit'}
            onClick={handleSubmit}
            disabled={isAppointmentSubmitDisabled}
            className={`w-full sm:w-auto justify-center px-6 py-2 rounded-lg font-medium flex items-center gap-2 transition-all shadow-sm ${isAppointmentSubmitDisabled ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-gradient-to-r from-green-600 to-emerald-500 text-white hover:from-green-700 hover:to-emerald-600 shadow-green-200'}`}
          >
            {creating ? <><Loader2 className="w-4 h-4 animate-spin" />Creating...</> : <><Calendar className="w-4 h-4" />{recurrenceRule.enabled && recurringOccurrences.length > 1 ? `Create ${recurringOccurrences.length} Recurring Slots` : isAssignmentLockedToCurrentUser ? 'Create My Appointment' : formData.assigned_team ? 'Create Team Appointment' : formData.assigned_to.length > 1 ? `Create ${formData.assigned_to.length} Appointments` : 'Create Appointment'}</>}
          </button>
        </ModalFooter>
      </div>
      {creating ? (
        <div
          className="fixed inset-0 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm dark:bg-slate-950/55"
          style={{ zIndex: APPOINTMENT_CREATE_LOADING_OVERLAY_Z }}
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <div
            className={`rounded-xl border p-8 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
          >
            <BrandedLoadingSpinner
              mode="default"
              size="md"
              message="Creating appointment…"
              subMessage="Please keep this window open."
              primaryColor="#059669"
              className="py-8"
            />
          </div>
        </div>
      ) : null}

      {selectedContact && (
        <WorkItemDetailModal
          isOpen={showStakeholderOverview}
          onClose={() => setShowStakeholderOverview(false)}
          workItem={buildStakeholderContactWorkItem(selectedContact as Parameters<typeof buildStakeholderContactWorkItem>[0])}
          visibleTabs={['overview', 'stakeholder_people', 'stakeholder_locations', 'stakeholder_documents', 'stakeholder_hierarchy', 'stakeholder_time_tracking', 'stakeholder_assets', 'history']}
          initialTab="overview"
          moduleColorKey="total-operations"
          containerZIndex={140}
        />
      )}
    </div>
  );

  if (disablePortal) {
    return modalContent;
  }

  return typeof document !== 'undefined' ? createPortal(
    modalContent,
    document.body
  ) : null;
}
